import {
  CAMERA_RENDER_CONFIG,
  CHARACTER_MOTION_CONFIG,
  ENVIRONMENT_RENDER_CONFIG,
  MINIMAP_CONFIG,
  MOVEMENT_TUNING,
  RENDER_QUALITY_CONFIG,
  STREET_LOCATION_CONFIG,
} from "../config/tuning.ts";
import {
  FULL_MAP_CONFIG,
  fullMapPlacePresentation,
  loadMapMarker,
  persistMapMarker,
} from "../config/map.ts";
import {
  loadMinimapPreferences,
  persistMinimapPreferences,
  type MinimapPreferences,
} from "../config/minimap.ts";
import {
  createBrowserAudioSystem,
  createBrowserRadioSystem,
} from "../audio/index.ts";
import type {
  AudioUpdateReport,
  RiverIdentity,
} from "../audio/index.ts";
import { SIMULATION_TIMING } from "../config/simulation.ts";
import {
  TILE_STREAMING_CONFIG,
  TILE_STREAMING_LOD_REFRESH_DISTANCE_M,
} from "../config/streaming.ts";
import {
  loadWorldViewSettings,
  persistWorldViewSettings,
  resetWorldViewProfile,
  streamingProfileFromViewSettings,
  updateWorldViewSetting,
  type WorldViewSettings,
} from "../config/viewSettings.ts";
import {
  loadRadioPreferences,
  persistRadioPreferences,
  RADIO_STATIONS,
  type RadioStationId,
} from "../config/radio.ts";
import { NEIGHBORHOOD_CONFIG } from "../config/neighborhood.ts";
import {
  VILNIUS_AUTHORED_ASSET_BACKGROUND_START_DELAY_MS,
  VILNIUS_AUTHORED_ASSET_INITIAL_RADIUS_M,
  VILNIUS_LANDMARK_MANIFEST_URL,
} from "../config/landmarks.ts";
import {
  AUTHORED_INTERIORS,
  authoredInteriorById,
  authoredInteriorExcludesAmbientPosition,
  authoredInteriorWorldPoint,
} from "../config/interiors.ts";
import { isTvTowerObservationPosition } from "../config/elevators.ts";
import {
  VILNIUS_BRIDGE_COLLISION_MANIFEST_URL,
  VILNIUS_BRIDGE_MANIFEST_URL,
} from "../config/bridges.ts";
import {
  ROAD_VEHICLE_REVIEW_DESTINATION,
  ROAD_VEHICLE_TUNING,
  VILNIUS_PARKED_VEHICLE_MANIFEST_URL,
  VILNIUS_ROAD_VEHICLES,
} from "../config/vehicles.ts";
import { VILNIUS_CIVIC_SQUARE_MANIFEST_URL } from "../config/civicSquares.ts";
import { VILNIUS_AIRPORT_MANIFEST_URL } from "../config/airport.ts";
import {
  VILNIUS_MODULAR_CITY_ASSET_MANIFEST_URL,
} from "../config/modularCityAssets.ts";
import {
  TRAVERSAL_DISCOVERY_VISIBLE_SECONDS,
  TRAVERSAL_DISCOVERY_SITES,
  TRAVERSAL_GATES,
  TRAVERSAL_REVIEW_DESTINATIONS,
  ROOFTOP_SHORTCUT_COLLIDER_IDS,
  TRAVERSAL_STATIC_COLLIDERS,
  VILNIUS_TRAVERSAL_MANIFEST_URL,
} from "../config/traversal.ts";
import {
  BOAT_TUNING,
  NERIS_BOAT_ASSET_MANIFEST_URL,
} from "../config/boats.ts";
import {
  PUBLIC_TRANSPORT_AUTHORED_BRIDGE_IDS,
  PUBLIC_TRANSPORT_TUNING,
  PUBLIC_TRANSPORT_VEHICLE_ASSETS,
} from "../config/publicTransport.ts";
import { RUNTIME_TILE_MANIFEST_URLS } from "../config/world.ts";
import { PlayerInputAdapter } from "../input/playerInput.ts";
import { createNeutralInputIntent } from "../input/intents.ts";
import { MultiplayerClient } from "../net/client.ts";
import {
  MULTIPLAYER_CONFIG,
  MULTIPLAYER_SOLO_SESSION_KEY,
} from "../config/multiplayer.ts";
import type { RoomCommand } from "../net/protocol.ts";
import {
  createGameRenderer,
  type GameRenderer,
  type GameRenderSnapshot,
  type RemotePlayerPresentation,
} from "../render/index.ts";
import type { EnvironmentSeason } from "../render/environment.ts";
import {
  createVilniusPhysicsWorld,
  type CharacterContact,
  type PhysicsWorldStats,
  type VilniusPhysicsWorld,
} from "../physics/index.ts";
import { createInitialGameState, type GameState } from "../sim/state.ts";
import type { SimulationEvent } from "../sim/events.ts";
import {
  completeGameTick,
  planGameTick,
} from "../sim/tick.ts";
import {
  applyBoatPlayerTick,
  boatInteractionPrompt,
  boatPresentations,
  createInitialBoatFleetState,
  mountedBoatId,
  stepBoatFleet,
} from "../sim/boats/index.ts";
import {
  applyRoadVehiclePlayerTick,
  clearRoadVehicleOccupancy,
  createInitialRoadVehicleFleetState,
  mountedRoadVehicle,
  placePlayerInRoadVehicle,
  roadVehicleInteractionPrompt,
  roadVehiclePlayerOverride,
  roadVehiclePresentations,
  stepRoadVehicleFleet,
  type RoadVehicleFleetState,
  type RoadVehicleQueries,
  type RoadVehicleSurfaceKind,
} from "../sim/vehicles/index.ts";
import {
  parseRoadVehicleFleet,
  ROAD_VEHICLE_SAVE_KEY,
  serializeRoadVehicleFleet,
} from "../sim/vehicles/save.ts";
import {
  applyPublicTransportPlayerTick,
  clearPublicTransportOccupancy,
  createInitialPublicTransportFleetState,
  mountedPublicTransport,
  publicTransportColliderPresentations,
  publicTransportInteractionPrompt,
  publicTransportPlayerOverride,
  publicTransportPresentations,
  publicTransportTripPresentationAtTime,
  stepPublicTransportFleet,
  type PublicTransportFleetState,
  type PublicTransportQueries,
  type PublicTransportVehiclePresentation,
} from "../sim/publicTransport/index.ts";
import {
  parsePublicTransportFleet,
  PUBLIC_TRANSPORT_SAVE_KEY,
  serializePublicTransportFleet,
} from "../sim/publicTransport/save.ts";
import { VILNIUS_PUBLIC_TRANSPORT_PATTERNS } from
  "../world/publicTransportRoutes.ts";
import { yieldResidentsToEmergencyVehicles } from "../sim/vehicles/emergencyYield.ts";
import {
  applyNeighborhoodAction,
  createNeighborhoodState,
  dialogueForResident,
  namedResidentPresentations,
  NAMED_RESIDENTS,
  NEIGHBORHOOD_SAVE_KEY,
  parseNeighborhoodSave,
  serializeNeighborhoodSave,
  stableNearbyResidentPresentations,
  type NeighborhoodAction,
  type NeighborhoodState,
  type ResidentPresentation,
} from "../sim/neighborhood/index.ts";
import { residentSeedForAppliedRoomSnapshot } from "./multiplayerResidents.ts";
import {
  createTraversalState,
  discoverTraversalSites,
  nearestLockedTraversalGate,
  parseTraversalSave,
  serializeTraversalSave,
  TRAVERSAL_SAVE_KEY,
  type LockedTraversalGate,
  type TraversalState,
} from "../sim/traversal/index.ts";
import {
  createInteriorPresenceState,
  stepInteriorPresence,
  type InteriorPresenceState,
} from "../sim/interiors/index.ts";
import {
  applyElevatorPlayerTick,
  createElevatorRideState,
  elevatorRidePresentation,
  elevatorInteractionPrompt,
  stepElevatorRide,
  type ElevatorRideState,
} from "../sim/elevators/index.ts";
import {
  createAppearanceCustomizer,
  createDevMenu,
  createDialogueUi,
  createElevatorUi,
  createFullMap,
  createHud,
  loadFullMapOverview,
  formatLocomotionStatusLabel,
  createMobileControls,
  createMinimap,
  createMultiplayerUi,
  createRadioUi,
  createTraversalDiscoveryUi,
  type AppearanceCustomizer,
  type DevMenu,
  type FullMap,
  type FullMapDiagnostics,
  type FullMapViewport,
  type MinimapDiagnostics,
  type MinimapSuppressionReason,
  type MultiplayerUi,
  type RadioUi,
} from "../ui/index.ts";
import {
  predictMultiplayerRoomSnapshot,
  type MultiplayerRoomSnapshot,
} from "../sim/session/index.ts";
import {
  DEFAULT_PLAYER_APPEARANCE,
  generateCharacterAppearance,
  parsePlayerAppearance,
  PLAYER_APPEARANCE_SAVE_KEY,
  sameCharacterAppearance,
  serializePlayerAppearance,
  type CharacterAppearance,
} from "../sim/appearance.ts";
import { createBrowserRuntimeTileSource } from "../workers/index.ts";
import {
  boundsContainPoint,
  pointInRing,
  ringCentroid,
  signedRingAreaM2,
  type HorizontalBounds,
} from "../world/geometry.ts";
import {
  parseRuntimeTileManifest,
  type RuntimeTileManifestV2,
  type RuntimeWorldTilePayloadV2,
} from "../world/schema/runtimeTile.ts";
import {
  parseDistrictVisualSample,
  type DistrictVisualSample,
} from "../world/schema/districtSample.ts";
import {
  worldTileAddressAt,
  worldTileId,
  type WorldTileId,
} from "../world/schema/tile.ts";
import { sampleTerrainTileData } from "../world/terrain.ts";
import {
  shouldRefreshTileSelection,
  TileStreamingController,
  type ReadyTileSnapshot,
} from "../world/streaming/index.ts";
import {
  buildPedestrianGraph,
  nearestPointOnPedestrianGraph,
  stableNearbyPedestrianNodes,
  type PedestrianGraph,
} from "../world/pedestrianGraph.ts";
import {
  updateStreetLocation,
  type StreetLocation,
} from "../world/streetLocation.ts";
import {
  extractMinimapTileFeatures,
  minimapTileIntersectsCircle,
  type MinimapTileFeatures,
} from "../world/minimap.ts";
import {
  loadLandmarkTeleportDestinations,
} from "../world/landmarkDestinations.ts";
import {
  loadAuthoredBridgeCollisions,
  sampleAuthoredBridgeSurface,
} from "../world/bridgeCollisions.ts";
import {
  loadAuthoredCivicSquareCollisions,
} from "../world/civicSquareCollisions.ts";
import { createGameLoop } from "./gameLoop.ts";

let LOCAL_PLAYER_ID = "local-player";
const MILESTONE = "M10";
const PANEL_SAMPLE_URL = "/world/samples/m4-zirmunai-panel.json";
const STREET_BOOKMARK = Object.freeze({
  // Fourth point of the checked-in official GRPK
  // `L. Stuokos-Gucevičiaus g.` route: a denser street-wall composition.
  x: 456.129,
  z: 390.24,
});
const STREET_ROUTE_NEXT = Object.freeze({
  // Following source point on the same route, used only for view orientation.
  x: 422.262,
  z: 443.157,
});
const VISTA_BOOKMARK = Object.freeze({
  // Source-terrain high point with a clear line toward the landmark.
  x: 864,
  z: 176,
});
const THREE_CROSSES_LANDMARK = Object.freeze({
  // Wikidata Q83238 transformed to EPSG:3346. Provisional local elevation is
  // the official GDR50LT interpolation recorded in the M4 source manifest.
  x: 1_261.914,
  y: 70,
  z: 161.8,
});

export interface RuntimeScenario {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly bookmark: string;
  readonly environment: {
    readonly season: EnvironmentSeason;
    readonly timeOfDayHours: number;
  };
}

export interface VilniusRuntimeState {
  readonly activeScenarioId: string;
  readonly multiplayer: {
    readonly mode: "entry" | "solo" | "room";
    readonly connectionStatus: string;
    readonly roomId: string | null;
    readonly playerId: string | null;
    readonly lastInputMoveX: number;
    readonly lastInputMoveZ: number;
    readonly keyboardAttached: boolean;
    readonly pressedKeyboardCodes: readonly string[];
    readonly reconciliationSampleCount: number;
    readonly reconciliationP95Meters: number;
    readonly reconciliationMaximumMeters: number;
    readonly reconciliationMaximumSample: Readonly<{
      distanceMeters: number;
      authoritativeTick: number;
      presentedTick: number;
      pendingIntentCount: number;
      from: Readonly<{ x: number; y: number; z: number }>;
      to: Readonly<{ x: number; y: number; z: number }>;
    }> | null;
  };
  readonly player: {
    readonly position: {
      readonly x: number;
      readonly y: number;
      readonly z: number;
    };
    readonly facingYaw: number;
    readonly lookYaw: number;
    readonly velocity: {
      readonly x: number;
      readonly y: number;
      readonly z: number;
    };
    readonly locomotion: {
      readonly mode: string;
      readonly grounded: boolean;
      readonly surfaceId: string | null;
      readonly groundSlopeRadians: number;
      readonly landingCount: number;
      readonly mantleCount: number;
      readonly footstepSequence: number;
    };
  };
  readonly frame: {
    readonly fps: number;
    readonly sampleCount: number;
    readonly drawCalls: number;
    readonly triangles: number;
    readonly geometries: number;
    readonly textures: number;
    readonly shaderPrograms: number;
    readonly frameCpuMs: number;
    readonly sceneUpdateCpuMs: number;
    readonly drawCpuMs: number;
    readonly shadowMapUpdateCount: number;
    readonly shadowMapUpdatedLastFrame: boolean;
  };
  readonly camera: {
    readonly collisionEnabled: boolean;
    readonly excludedRoadVehicleColliderCount: number;
    readonly farMeters: number;
    readonly lookYaw: number;
  };
  readonly streaming: {
    readonly desiredTiles: number;
    readonly queuedTiles: number;
    readonly loadingTiles: number;
    readonly readyTiles: number;
    readonly readyLod0Tiles: number;
    readonly readyTileLods: readonly {
      readonly tileId: WorldTileId;
      readonly lod: number;
    }[];
    readonly readyBytes: number;
    readonly evictions: number;
    readonly disposedPayloads: number;
    readonly attachedRenderTiles: number;
    readonly maximumTileAttachMs: number;
    readonly slowestAttachedTileId: WorldTileId | null;
    readonly slowestTileBuildCpuMsByGroup: Readonly<Record<string, number>>;
    readonly lastUpdateMs: number;
    readonly lastSettleMs: number;
    readonly lastRenderQueueMs: number;
    readonly renderDrawCallEstimate: number;
    readonly renderTriangleCount: number;
    readonly renderGeometryCount: number;
    readonly renderDrawCallsByGroup: Readonly<Record<string, number>>;
    readonly renderTrianglesByGroup: Readonly<Record<string, number>>;
    readonly renderBuildCpuMsByGroup: Readonly<Record<string, number>>;
    readonly renderBuildCpuMsByTile: Readonly<
      Record<string, Readonly<Record<string, number>>>
    >;
  };
  readonly physics: PhysicsWorldStats & {
    readonly lastContactCount: number;
    readonly lastEvents: readonly SimulationEvent[];
  };
  readonly environment: {
    readonly season: string;
    readonly timeOfDayHours: number;
    readonly frozen: boolean;
    readonly surfaceWetness01: number;
    readonly nightFactor: number;
    readonly fogFarMeters: number;
  };
  readonly viewSettings: Readonly<WorldViewSettings>;
  readonly location: {
    readonly streetName: string | null;
    readonly distanceM: number | null;
  };
  readonly minimap: MinimapDiagnostics & {
    readonly extractionErrorCount: number;
    readonly lastExtractionMs: number;
    readonly maximumExtractionMs: number;
  };
  readonly map: FullMapDiagnostics;
  readonly audio: {
    readonly available: boolean;
    readonly resumed: boolean;
    readonly muted: boolean;
    readonly radioStationId: RadioStationId | null;
    readonly radioPlaybackState: string;
    readonly radioVolume: number;
    readonly lastStartedEventCount: number;
    readonly totalStartedEventCount: number;
    readonly windLevel: number;
    readonly riverLevel: number;
    readonly interiorLevel: number;
    readonly vehicleEngineLevel: number;
    readonly vehicleTyreLevel: number;
    readonly vehicleHornLevel: number;
    readonly vehicleSirenLevel: number;
  };
  readonly interior: {
    readonly activeInteriorId: string | null;
    readonly candidateInteriorId: string | null;
    readonly entryCount: number;
    readonly exitCount: number;
    readonly lightingMix01: number;
  };
  readonly elevator: {
    readonly phase: "idle" | "travelling";
    readonly fromStopId: string | null;
    readonly toStopId: string | null;
    readonly progress01: number;
    readonly rideCount: number;
  };
  readonly visualStudy: {
    readonly panelSampleId: string | null;
    readonly threeCrossesPlaced: boolean;
    readonly heroLandmarkCount: number;
    readonly loadedHeroLandmarkIds: readonly string[];
    readonly simplifiedHeroLandmarkIds: readonly string[];
    readonly authoredBridgeCount: number;
    readonly electricBoatCount: number;
    readonly publicTransportAssetModeCount: number;
    readonly publicTransportSourceTriangleCount: number;
    readonly parkedVehicleCount: number;
    readonly parkedVehicleMeshCount: number;
    readonly civicSquareCount: number;
    readonly airportAssetCount: number;
    readonly airportMeshCount: number;
    readonly traversalAssetCount: number;
    readonly modularCityArchetypeCount: number;
    readonly modularCityPlacementCount: number;
    readonly modularCityFullDetailArchetypeCount: number;
    readonly modularCityBucketCount: number;
    readonly modularCityVisibleBucketCount: number;
    readonly modularCityFullDetailBucketCount: number;
    readonly modularCityMeshCount: number;
    readonly modularCityInstancedTriangleCount: number;
  };
  readonly boats: {
    readonly mountedBoatId: string | null;
    readonly vessels: readonly {
      readonly id: string;
      readonly position: Readonly<{ x: number; y: number; z: number }>;
      readonly yawRadians: number;
      readonly speedMps: number;
      readonly occupied: boolean;
    }[];
  };
  readonly roadVehicles: {
    readonly mountedVehicleId: string | null;
    readonly mountedSeatId: string | null;
    readonly mountedSeatRole: "driver" | "passenger" | null;
    readonly mountedTransitionKind: "entering" | "exiting" | null;
    readonly vehicles: readonly {
      readonly id: string;
      readonly position: Readonly<{ x: number; y: number; z: number }>;
      readonly yawRadians: number;
      readonly pitchRadians: number;
      readonly rollRadians: number;
      readonly speedMps: number;
      readonly steeringRadians: number;
      readonly occupied: boolean;
    }[];
  };
  readonly publicTransport: {
    readonly serviceTimeSeconds: number;
    readonly mountedVehicleId: string | null;
    readonly mountedPatternId: string | null;
    readonly alightRequested: boolean;
    readonly renderedVehicleCount: number;
    readonly colliderVehicleCount: number;
    readonly vehicles: readonly {
      readonly id: string;
      readonly patternId: string;
      readonly mode: "bus" | "trolleybus";
      readonly routeShortName: string;
      readonly headsign: string;
      readonly position: Readonly<{ x: number; y: number; z: number }>;
      readonly yawRadians: number;
      readonly speedMps: number;
      readonly doorsOpen: boolean;
      readonly currentStopId: string | null;
      readonly nextStopId: string | null;
      readonly occupied: boolean;
    }[];
  };
  readonly neighborhood: {
    readonly namedResidentCount: number;
    readonly nearbyFullSimulationCount: number;
    readonly ambientPopulationCount: number;
    readonly unloadedNamedResidentCount: number;
    readonly activeDialogueResidentId: string | null;
    readonly presentedResidents: readonly {
      readonly id: string;
      readonly named: boolean;
      readonly position: Readonly<{ x: number; y: number; z: number }>;
    }[];
    readonly state: NeighborhoodState;
  };
  readonly traversal: {
    readonly discoveredSiteIds: readonly string[];
    readonly nearestLockedGateId: string | null;
  };
}

export interface VilniusRuntimeBridge {
  ready: boolean;
  applyScenario(scenario: RuntimeScenario): Promise<void>;
  loadDeferredAuthoredAssets(): Promise<void>;
  moveToEvidencePoint(
    position: Readonly<{ x: number; z: number }>,
    facingYaw?: number,
  ): Promise<void>;
  preparePublicTransportReview(
    sourceRouteId: string,
    directionId?: 0 | 1,
  ): Promise<void>;
  setPublicTransportReviewTime(serviceTimeSeconds: number): void;
  faceEvidenceYaw(facingYaw: number): void;
  pitchEvidenceCamera(deltaPixelsY: number): void;
  resetEvidenceCameraPitch(): void;
  resetMultiplayerReconciliationDiagnostics(): void;
  getRenderedPlayerPosition(): Readonly<{ x: number; y: number; z: number }>;
  getRenderedMotionDiagnostics(): ReturnType<
    GameRenderer["getRenderedMotionDiagnostics"]
  >;
  applyNeighborhoodAction(action: NeighborhoodAction): void;
  openResidentDialogue(residentId: string): void;
  resetNeighborhood(): void;
  getState(): VilniusRuntimeState;
}

export interface VilniusApplication {
  readonly bridge: VilniusRuntimeBridge;
  dispose(): void;
}

function requireElement<T extends Element>(
  selector: string,
  constructor: { new (): T },
): T {
  const element = document.querySelector(selector);
  if (!(element instanceof constructor)) {
    throw new Error(`Required element ${selector} is missing.`);
  }
  return element;
}

function toRenderSnapshot(
  state: Readonly<GameState>,
  roadVehicles?: Readonly<RoadVehicleFleetState>,
  interiorCameraDistanceM?: number,
  publicTransport?: Readonly<PublicTransportFleetState>,
): GameRenderSnapshot {
  const player = state.playersById[LOCAL_PLAYER_ID];
  if (player === undefined) {
    throw new Error(`Player ${LOCAL_PLAYER_ID} is missing from game state.`);
  }
  const mounted = roadVehicles === undefined
    ? null
    : mountedRoadVehicle(roadVehicles, LOCAL_PLAYER_ID);
  const transit = publicTransport === undefined
    ? null
    : mountedPublicTransport(publicTransport, LOCAL_PLAYER_ID);

  return {
    tick: state.tick,
    timeSeconds: state.simulationTimeSeconds,
    player: {
      id: player.id,
      position: player.position,
      yawRadians: player.facingYaw,
      lookYawRadians: player.lookYaw,
      planarSpeedMetersPerSecond: Math.hypot(
        player.velocity.x,
        player.velocity.z,
      ),
      verticalSpeedMetersPerSecond: player.velocity.y,
      grounded: player.locomotion.grounded,
      isFlying: player.locomotion.mode === "flying",
      usesAerialVisibility: isTvTowerObservationPosition(player.position),
      isSprinting: player.isSprinting,
      cameraTargetHeightOffsetMeters:
        mounted !== null
          ? mounted.spec.cameraTargetHeightOffsetM
          : transit !== null
          ? PUBLIC_TRANSPORT_TUNING.passengerCameraTargetHeightM
          : player.locomotion.surfaceId?.startsWith("boat:") === true
          ? BOAT_TUNING.cameraTargetHeightOffsetM
          : 0,
      ...(mounted !== null
        ? { cameraDistanceMeters: mounted.spec.cameraDistanceM }
        : transit !== null
        ? { cameraDistanceMeters: PUBLIC_TRANSPORT_TUNING.passengerCameraDistanceM }
        : interiorCameraDistanceM === undefined
        ? {}
        : { cameraDistanceMeters: interiorCameraDistanceM }),
      vehicleSeatRole: mounted?.seat.role ?? (transit === null ? null : "passenger"),
      mountedVehicleId: mounted?.vehicle.id ?? transit?.presentation.id ?? null,
      ...(mounted !== null
        ? {
            vehicleSpeedMetersPerSecond: Math.abs(mounted.vehicle.speedMps),
            vehicleHeadingYawRadians: mounted.vehicle.yawRadians,
            vehicleSteeringRadians: mounted.vehicle.steeringRadians,
            vehicleLookBehind: mounted.vehicle.lookBehindActive,
          }
        : transit !== null
        ? {
            vehicleSpeedMetersPerSecond:
              Math.abs(transit.presentation.speedMps),
            vehicleHeadingYawRadians: transit.presentation.yawRadians,
            vehicleSteeringRadians: 0,
            vehicleLookBehind: false,
          }
        : {}),
    },
  };
}

function stateAt(
  position: Readonly<{ x: number; y: number; z: number }>,
  facingYaw = 0,
): GameState {
  const initial = createInitialGameState(LOCAL_PLAYER_ID);
  const player = initial.playersById[LOCAL_PLAYER_ID];
  if (player === undefined) {
    throw new Error(`Player ${LOCAL_PLAYER_ID} is missing from game state.`);
  }
  return {
    ...initial,
    playersById: {
      ...initial.playersById,
      [LOCAL_PLAYER_ID]: {
        ...player,
        position: { ...position },
        facingYaw,
        lookYaw: facingYaw,
      },
    },
  };
}

async function loadRuntimeTileManifest(): Promise<RuntimeTileManifestV2> {
  for (
    const [index, manifestUrl] of RUNTIME_TILE_MANIFEST_URLS.entries()
  ) {
    const response = await fetch(manifestUrl);
    const isLast = index === RUNTIME_TILE_MANIFEST_URLS.length - 1;
    const contentType = response.headers.get("content-type") ?? "";
    if (
      response.ok &&
      contentType.toLowerCase().includes("application/json")
    ) {
      return parseRuntimeTileManifest(await response.json());
    }
    if (
      isLast ||
      (response.status !== 404 && response.ok === false)
    ) {
      throw new Error(
        `Could not load the runtime tile manifest ${manifestUrl} (${response.status}).`,
      );
    }
  }
  throw new Error("No runtime tile manifest URL is configured.");
}

function runtimeManifestBounds(
  manifest: Readonly<RuntimeTileManifestV2>,
): HorizontalBounds {
  const first = manifest.tiles[0];
  if (first === undefined) {
    throw new Error("The runtime tile manifest must contain map bounds.");
  }
  return manifest.tiles.slice(1).reduce<HorizontalBounds>(
    (bounds, entry) => ({
      minX: Math.min(bounds.minX, entry.bounds.minX),
      minZ: Math.min(bounds.minZ, entry.bounds.minZ),
      maxX: Math.max(bounds.maxX, entry.bounds.maxX),
      maxZ: Math.max(bounds.maxZ, entry.bounds.maxZ),
    }),
    { ...first.bounds },
  );
}

function horizontalBoundsIntersect(
  left: Readonly<HorizontalBounds>,
  right: Readonly<HorizontalBounds>,
): boolean {
  return left.minX <= right.maxX && left.maxX >= right.minX &&
    left.minZ <= right.maxZ && left.maxZ >= right.minZ;
}

async function loadPanelSample(): Promise<DistrictVisualSample> {
  const response = await fetch(PANEL_SAMPLE_URL);
  if (!response.ok) {
    throw new Error(
      `Could not load the M4 Žirmūnai style sample (${response.status}).`,
    );
  }
  return parseDistrictVisualSample(await response.json());
}

function yawToward(
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
): number {
  return Math.atan2(to.x - from.x, to.z - from.z);
}

function findCourtyardBookmark(
  readyTiles: readonly Readonly<ReadyTileSnapshot>[],
  manifest: Readonly<RuntimeTileManifestV2>,
): Readonly<{ x: number; z: number; facingYaw: number }> | null {
  type CourtyardBookmarkCandidate = {
    x: number;
    z: number;
    facingYaw: number;
    distanceM: number;
  };
  let nearest: CourtyardBookmarkCandidate | null = null;
  let nearestFallback: CourtyardBookmarkCandidate | null = null;
  for (const ready of readyTiles) {
    const tile = ready.payload;
    for (const building of tile.buildings) {
      const hole = building.footprint.holes[0];
      if (hole !== undefined) {
        const areaM2 = Math.abs(signedRingAreaM2(hole));
        const centroid = ringCentroid(hole);
        let longestEdge = { x: 0, z: 1, lengthM: 0 };
        for (let index = 0; index < hole.length - 1; index += 1) {
          const from = hole[index];
          const to = hole[index + 1];
          if (from === undefined || to === undefined) {
            continue;
          }
          const x = to.x - from.x;
          const z = to.z - from.z;
          const lengthM = Math.hypot(x, z);
          if (lengthM > longestEdge.lengthM) {
            longestEdge = { x, z, lengthM };
          }
        }
        const principal = longestEdge.lengthM > 0
          ? {
              x: longestEdge.x / longestEdge.lengthM,
              z: longestEdge.z / longestEdge.lengthM,
            }
          : { x: 0, z: 1 };
        // Stage the character back along the courtyard's long axis and face
        // across the open court. Leave roughly eight metres behind the player
        // for the third-person camera so the evidence view shows the whole
        // enclosure instead of staring directly into its nearest wall.
        const forwardOffsetM = Math.max(
          0,
          Math.min(5, longestEdge.lengthM / 2 - 8),
        );
        const staged = {
          x: centroid.x - principal.x * forwardOffsetM,
          z: centroid.z - principal.z * forwardOffsetM,
        };
        const relative = pointInRing(staged, hole) ? staged : centroid;
        if (!pointInRing(relative, hole)) {
          continue;
        }
        const candidate = {
          x: tile.origin.x + relative.x,
          z: tile.origin.z + relative.z,
        };
        if (
          !manifest.tiles.some((entry) =>
            boundsContainPoint(entry.bounds, candidate, true)
          )
        ) {
          continue;
        }
        const distanceM = Math.hypot(
          candidate.x - STREET_BOOKMARK.x,
          candidate.z - STREET_BOOKMARK.z,
        );
        const bookmark = {
          ...candidate,
          facingYaw: Math.atan2(principal.x, principal.z),
          distanceM,
        };
        if (
          nearestFallback === null ||
          distanceM < nearestFallback.distanceM
        ) {
          nearestFallback = bookmark;
        }
        // Tiny Old Town lightwells are real, but they are a poor camera and
        // scale benchmark. Prefer the nearest source courtyard with enough
        // open floor for the default third-person rig, keeping the closest
        // valid hole as a fallback for reduced development fixtures.
        if (
          areaM2 >= 250 &&
          (nearest === null || distanceM < nearest.distanceM)
        ) {
          nearest = bookmark;
        }
      }
    }
  }
  const selected = nearest ?? nearestFallback;
  return selected === null
    ? null
    : {
        x: selected.x,
        z: selected.z,
        facingYaw: selected.facingYaw,
      };
}

function sampleReadyTerrain(
  readyTiles: readonly Readonly<ReadyTileSnapshot>[],
  position: Readonly<{ x: number; z: number }>,
): number | null {
  for (const ready of readyTiles) {
    const tile = ready.payload;
    if (!boundsContainPoint(tile.bounds, position, true)) {
      continue;
    }
    const height = sampleTerrainTileData(tile.terrain, tile.origin, position);
    if (height !== null) {
      return height;
    }
  }
  return null;
}

function withPlayerHeight(state: Readonly<GameState>, height: number): GameState {
  const player = state.playersById[LOCAL_PLAYER_ID];
  if (player === undefined || player.position.y === height) {
    return state;
  }
  return {
    ...state,
    playersById: {
      ...state.playersById,
      [LOCAL_PLAYER_ID]: {
        ...player,
        position: {
          ...player.position,
          y: height,
        },
      },
    },
  };
}

function loadNeighborhoodState(storage: Readonly<Storage>): NeighborhoodState {
  const raw = storage.getItem(NEIGHBORHOOD_SAVE_KEY);
  if (raw === null) {
    return createNeighborhoodState();
  }
  try {
    return parseNeighborhoodSave(raw);
  } catch {
    storage.removeItem(NEIGHBORHOOD_SAVE_KEY);
    return createNeighborhoodState();
  }
}

function loadPlayerAppearance(storage: Readonly<Storage>): CharacterAppearance {
  const raw = storage.getItem(PLAYER_APPEARANCE_SAVE_KEY);
  if (raw === null) {
    return structuredClone(DEFAULT_PLAYER_APPEARANCE);
  }
  try {
    return parsePlayerAppearance(raw);
  } catch {
    storage.removeItem(PLAYER_APPEARANCE_SAVE_KEY);
    return structuredClone(DEFAULT_PLAYER_APPEARANCE);
  }
}

function loadTraversalState(storage: Readonly<Storage>): TraversalState {
  const raw = storage.getItem(TRAVERSAL_SAVE_KEY);
  if (raw === null) {
    return createTraversalState();
  }
  try {
    return parseTraversalSave(raw);
  } catch {
    storage.removeItem(TRAVERSAL_SAVE_KEY);
    return createTraversalState();
  }
}

function withTerrainHeight(
  resident: Readonly<ResidentPresentation>,
  readyTiles: readonly Readonly<ReadyTileSnapshot>[],
  cachedHeights: Map<string, number>,
): ResidentPresentation | null {
  const sampled = sampleReadyTerrain(readyTiles, resident.position);
  if (sampled !== null) {
    cachedHeights.set(resident.id, sampled);
  }
  const height = sampled ?? cachedHeights.get(resident.id);
  if (height === undefined) {
    return null;
  }
  return {
    ...resident,
    position: {
      ...resident.position,
      y: height,
    },
  };
}

function compassDirection(deltaX: number, deltaZ: number): string {
  const directions = [
    "north",
    "north-east",
    "east",
    "south-east",
    "south",
    "south-west",
    "west",
    "north-west",
  ] as const;
  const radians = Math.atan2(deltaX, deltaZ);
  const index = Math.round(
    ((radians + Math.PI * 2) % (Math.PI * 2)) / (Math.PI / 4),
  ) % directions.length;
  return directions[index]!;
}

function stableTextHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

export async function bootstrapVilnius(): Promise<VilniusApplication> {
  const canvas = requireElement("#game-canvas", HTMLCanvasElement);
  const hudRoot = requireElement("#hud", HTMLElement);
  canvas.tabIndex = 0;

  const [physicsSetup, panelSample] = await Promise.all([
    Promise.all([
      loadAuthoredBridgeCollisions(
        VILNIUS_BRIDGE_COLLISION_MANIFEST_URL,
      ),
      loadAuthoredCivicSquareCollisions(
        VILNIUS_CIVIC_SQUARE_MANIFEST_URL,
      ),
    ]).then(async ([bridgeCollisions, civicSquareCollisions]) => ({
      bridgeCollisions,
      physics: await createVilniusPhysicsWorld({
        bridgeCollisions,
        authoredColliders: [
          ...TRAVERSAL_STATIC_COLLIDERS,
          ...civicSquareCollisions,
        ],
      }),
    })),
    loadPanelSample(),
  ]);
  const { physics, bridgeCollisions } = physicsSetup;
  const publicTransportBridgeIds = new Set<string>(
    PUBLIC_TRANSPORT_AUTHORED_BRIDGE_IDS,
  );
  const publicTransportBridgeCollisions = bridgeCollisions.filter((bridge) =>
    publicTransportBridgeIds.has(bridge.id)
  );
  let cameraExcludedRoadVehicleColliderIds: readonly string[] = [];
  const structuralCameraQuery = (segment: {
    readonly from: { readonly x: number; readonly y: number; readonly z: number };
    readonly to: { readonly x: number; readonly y: number; readonly z: number };
    readonly radiusMeters: number;
  }) => {
    const result = physics.castCamera({
      target: segment.from,
      desiredCameraPosition: segment.to,
      radiusM: segment.radiusMeters,
      excludePlayerId: LOCAL_PLAYER_ID,
      excludeColliderIds: cameraExcludedRoadVehicleColliderIds,
    });
    return {
      maximumDistanceMeters: result.obstructed
        ? result.resolvedDistanceM
        : null,
      obstructingObjectIds: result.obstructionIds,
    };
  };
  let worldViewSettings = loadWorldViewSettings(localStorage);
  const renderer = createGameRenderer({
    canvas,
    config: {
      camera: CAMERA_RENDER_CONFIG,
      quality: RENDER_QUALITY_CONFIG,
      characterMotion: CHARACTER_MOTION_CONFIG,
      environment: ENVIRONMENT_RENDER_CONFIG,
    },
    cameraCollisionQuery: structuralCameraQuery,
  });
  let playerAppearance = loadPlayerAppearance(localStorage);
  renderer.setPlayerAppearance(playerAppearance);
  renderer.setVisibilitySettings(worldViewSettings);
  renderer.setEnvironment({
    season: "summer",
    timeOfDayHours: 10,
    frozen: false,
  });
  renderer.setThreeCrossesLandmark(THREE_CROSSES_LANDMARK);
  const authoredAssetLoadOptions = Object.freeze({
    initialFocus: STREET_BOOKMARK,
    initialRadiusM: VILNIUS_AUTHORED_ASSET_INITIAL_RADIUS_M,
    deferDistant: true,
  });
  const [
    landmarkDiagnostics,
    bridgeDiagnostics,
    boatDiagnostics,
    publicTransportDiagnostics,
    parkedVehicleDiagnostics,
    civicSquareDiagnostics,
    airportDiagnostics,
    traversalDiagnostics,
    modularCityAssetDiagnostics,
    landmarkDestinations,
    fullMapOverview,
    manifest,
  ] = await Promise.all([
    renderer.loadVilniusLandmarks(
      VILNIUS_LANDMARK_MANIFEST_URL,
      authoredAssetLoadOptions,
    ),
    renderer.loadVilniusBridges(
      VILNIUS_BRIDGE_MANIFEST_URL,
      authoredAssetLoadOptions,
    ),
    renderer.loadBoatFleet(NERIS_BOAT_ASSET_MANIFEST_URL),
    renderer.loadPublicTransportFleet(),
    renderer.loadParkedVehicles(
      VILNIUS_PARKED_VEHICLE_MANIFEST_URL,
      authoredAssetLoadOptions,
    ),
    renderer.loadCivicSquares(
      VILNIUS_CIVIC_SQUARE_MANIFEST_URL,
      authoredAssetLoadOptions,
    ),
    renderer.loadVilniusAirport(
      VILNIUS_AIRPORT_MANIFEST_URL,
      authoredAssetLoadOptions,
    ),
    renderer.loadTraversalNetwork(
      VILNIUS_TRAVERSAL_MANIFEST_URL,
      authoredAssetLoadOptions,
    ),
    renderer.loadModularCityAssets(
      VILNIUS_MODULAR_CITY_ASSET_MANIFEST_URL,
    ),
    loadLandmarkTeleportDestinations(VILNIUS_LANDMARK_MANIFEST_URL),
    loadFullMapOverview(),
    loadRuntimeTileManifest(),
  ]);
  const fullMapPlaces = landmarkDestinations.map((destination) => ({
    id: destination.id,
    ...fullMapPlacePresentation(destination.id, destination.label),
    position: { ...(destination.mapPosition ?? destination.arrivalPosition) },
  }));
  const hud = createHud(hudRoot);
  const minimap = createMinimap(hudRoot);
  let minimapPreferences: MinimapPreferences =
    loadMinimapPreferences(localStorage);
  const mapWorldBounds = runtimeManifestBounds(manifest);
  let mapMarker = loadMapMarker(localStorage);
  if (
    mapMarker !== null &&
    !boundsContainPoint(mapWorldBounds, mapMarker, true)
  ) {
    mapMarker = null;
    persistMapMarker(localStorage, null);
  }
  const dialogueUi = createDialogueUi(hudRoot);
  const elevatorUi = createElevatorUi(hudRoot);
  const traversalDiscoveryUi = createTraversalDiscoveryUi(hudRoot);
  const audio = createBrowserAudioSystem();
  const radio = createBrowserRadioSystem({
    initialPreferences: loadRadioPreferences(localStorage),
    onPreferencesChange(preferences): void {
      persistRadioPreferences(localStorage, preferences);
    },
  });
  const input = new PlayerInputAdapter(window);
  input.attach();
  const mobileControls = createMobileControls(hudRoot, canvas, {
    onMove(moveX, moveZ): void {
      input.setTouchMovement(moveX, moveZ);
    },
    onLookDelta(deltaX, deltaY): void {
      input.rotateLookByPixels(deltaX);
      renderer.setLocalLookYaw(input.lookYaw());
      renderer.pitchBy(deltaY);
    },
    onJumpHeld(held): void {
      input.setTouchJumpHeld(held);
    },
    onDescendHeld(held): void {
      input.setTouchDescendHeld(held);
    },
    onSprintHeld(held): void {
      input.setTouchSprintHeld(held);
    },
    onInteract(): void {
      input.pressTouchInteract();
    },
    onToggleFlight(): void {
      input.pressTouchToggleFlight();
    },
    onCycleSeat(): void {
      input.pressTouchCycleSeat();
    },
    onEngage(): void {
      hasEngaged = true;
      instructionsVisible = false;
      void audio.resume();
      void radio.resume();
    },
  });

  const tileSource = createBrowserRuntimeTileSource();
  const fullMapTileSource = createBrowserRuntimeTileSource();
  let streamingErrorCount = 0;
  const streaming = new TileStreamingController({
    manifest,
    source: tileSource,
    config: TILE_STREAMING_CONFIG,
  });
  const physicsTilePayloads = new Map<
    WorldTileId,
    RuntimeWorldTilePayloadV2
  >();
  let lastContacts: readonly CharacterContact[] = [];
  let lastSimulationEvents: readonly SimulationEvent[] = [];
  let lastAudioReport: AudioUpdateReport = {
    tick: 0,
    startedEventIds: [],
    suppressedEvents: [],
    bellTriggered: false,
    ambience: { wind: 0, river: 0, interior: 0 },
    vehicle: { engine: 0, tyres: 0, horn: 0, siren: 0, impactTriggered: false },
  };
  let totalStartedAudioEventCount = 0;
  let panelSampleActive = false;
  let cameraCollisionEnabled = true;
  function setCameraCollisionEnabled(enabled: boolean): void {
    if (cameraCollisionEnabled === enabled) {
      return;
    }
    cameraCollisionEnabled = enabled;
    renderer.setCameraCollisionQuery(
      enabled ? structuralCameraQuery : undefined,
    );
  }
  function synchronizeCameraCollision(
    locomotionMode: string,
    isBoating: boolean,
    isInsideInterior: boolean,
  ): void {
    // Flight and authored interiors need an unrestricted camera. Casting from
    // inside a compound landmark can start on a floor or wall face and collapse
    // the third-person camera into the character before its collider is ignored.
    setCameraCollisionEnabled(
      !panelSampleActive &&
        locomotionMode !== "flying" &&
        !isBoating &&
        !isInsideInterior,
    );
  }
  let activeStreetLocation: StreetLocation | null = null;
  let lastStreetLocationProbe:
    | Readonly<{ x: number; z: number; tileKey: string }>
    | null = null;
  let neighborhood = loadNeighborhoodState(localStorage);
  let traversal = loadTraversalState(localStorage);
  let interiorPresence: InteriorPresenceState = createInteriorPresenceState();
  const authoredAmbientInteriors = AUTHORED_INTERIORS.filter(
    ({ ambientExclusionVolumes }) =>
      ambientExclusionVolumes !== undefined &&
      ambientExclusionVolumes.length > 0,
  );
  const foodHallInterior = authoredInteriorById("baltas-tiltas-food-hall");
  const foodHallAmbientCentre = foodHallInterior === null
    ? null
    : authoredInteriorWorldPoint(foodHallInterior, { x: 85, y: 0, z: 0 });
  let elevatorRide: ElevatorRideState = createElevatorRideState();
  let nearestLockedGate: LockedTraversalGate | null = null;
  let traversalNoticeUntilSeconds = 0;
  let lockedTraversalNoticeUntilSeconds = 0;
  let vehicleExitBlockedUntilSeconds = 0;

  function synchronizeTraversalAccess(): void {
    for (const gate of TRAVERSAL_GATES) {
      physics.setAuthoredColliderEnabled(gate.id, !neighborhood.access[gate.access]);
    }
    for (const colliderId of ROOFTOP_SHORTCUT_COLLIDER_IDS) {
      physics.setAuthoredColliderEnabled(
        colliderId,
        neighborhood.access.rooftopShortcut,
      );
    }
    renderer.setTraversalShortcutState(neighborhood.access);
  }
  synchronizeTraversalAccess();
  let activeDialogueResidentId: string | null = null;
  let nearestInteractableResidentId: string | null = null;
  let nearbyNamedResidentCount = 0;
  let ambientPopulationCount = 0;
  let activeNamedPresentations: readonly ResidentPresentation[] = [];
  let activeAmbientNodeIds: readonly string[] = [];
  const activeAmbientRouteByNodeId = new Map<
    string,
    Readonly<{ id: string; x: number; z: number }> | null
  >();
  const residentTerrainHeightById = new Map<string, number>();
  let lastResidentPresentations: readonly ResidentPresentation[] = [];
  let pedestrianGraph: PedestrianGraph = { nodes: [], nodeById: {} };
  let pedestrianGraphTileKey = "";
  let renderQueueTail: Promise<void> = Promise.resolve();
  let minimapQueueTail: Promise<void> = Promise.resolve();
  const minimapTiles = new Map<
    WorldTileId,
    Readonly<{
      payload: RuntimeWorldTilePayloadV2;
      features: MinimapTileFeatures;
    }>
  >();
  const fullMapTiles = new Map<
    WorldTileId,
    Readonly<{
      features: MinimapTileFeatures;
      lastUsed: number;
    }>
  >();
  const fullMapActiveLoads = new Map<WorldTileId, AbortController>();
  const fullMapQueuedIds = new Set<WorldTileId>();
  let fullMapLoadQueue: RuntimeTileManifestV2["tiles"][number][] = [];
  let fullMapDesiredIds = new Set<WorldTileId>();
  let fullMapLoadClock = 0;
  let fullMapFeatureRevision = 0;
  let fullMapLoadErrorCount = 0;
  let minimapTileRevision = 0;
  let minimapExtractionErrorCount = 0;
  let lastMinimapExtractionMs = 0;
  let maximumMinimapExtractionMs = 0;
  let maximumTileAttachMs = 0;
  let slowestAttachedTileId: WorldTileId | null = null;
  let slowestTileBuildCpuMsByGroup: Readonly<Record<string, number>> = {};
  let lastUpdateMs = 0;
  let lastSettleMs = 0;
  let lastRenderQueueMs = 0;

  function cacheFullMapFeatures(features: MinimapTileFeatures): void {
    fullMapLoadClock += 1;
    fullMapTiles.set(features.tileId, {
      features,
      lastUsed: fullMapLoadClock,
    });
    fullMapFeatureRevision += 1;
    if (fullMapTiles.size <= FULL_MAP_CONFIG.maxCachedTiles) return;
    const evictionCandidates = [...fullMapTiles.entries()]
      .filter(([tileId]) => !fullMapDesiredIds.has(tileId))
      .sort((left, right) => left[1].lastUsed - right[1].lastUsed);
    for (const [tileId] of evictionCandidates) {
      if (fullMapTiles.size <= FULL_MAP_CONFIG.maxCachedTiles) break;
      fullMapTiles.delete(tileId);
      fullMapFeatureRevision += 1;
    }
  }

  function pumpFullMapLoads(): void {
    while (
      fullMapActiveLoads.size < FULL_MAP_CONFIG.maxConcurrentLoads &&
      fullMapLoadQueue.length > 0
    ) {
      const entry = fullMapLoadQueue.shift();
      if (entry === undefined) break;
      fullMapQueuedIds.delete(entry.id);
      if (
        fullMapTiles.has(entry.id) ||
        fullMapActiveLoads.has(entry.id) ||
        !fullMapDesiredIds.has(entry.id)
      ) {
        continue;
      }
      const controller = new AbortController();
      fullMapActiveLoads.set(entry.id, controller);
      void fullMapTileSource.load({
        entry,
        lod: 2,
        signal: controller.signal,
      }).then((loaded) => {
        if (!disposed && !controller.signal.aborted) {
          cacheFullMapFeatures(extractMinimapTileFeatures(
            loaded.payload,
            MINIMAP_CONFIG.lineSimplificationToleranceM,
          ));
        }
      }).catch((error: unknown) => {
        const aborted = controller.signal.aborted ||
          (error instanceof DOMException && error.name === "AbortError") ||
          (error instanceof Error && error.name === "AbortError");
        if (!aborted && !disposed) {
          fullMapLoadErrorCount += 1;
          console.error("Could not prepare a full-map tile", error);
        }
      }).finally(() => {
        fullMapActiveLoads.delete(entry.id);
        pumpFullMapLoads();
      });
    }
  }

  function updateFullMapViewport(
    viewport: Readonly<FullMapViewport>,
  ): void {
    if (viewport.radiusM > FULL_MAP_CONFIG.detailedTileMaximumRadiusM) {
      suspendFullMapLoading();
      return;
    }
    const margin = FULL_MAP_CONFIG.tilePrefetchMarginM;
    const expandedBounds: HorizontalBounds = {
      minX: viewport.bounds.minX - margin,
      minZ: viewport.bounds.minZ - margin,
      maxX: viewport.bounds.maxX + margin,
      maxZ: viewport.bounds.maxZ + margin,
    };
    const entries = manifest.tiles
      .filter((entry) => horizontalBoundsIntersect(entry.bounds, expandedBounds))
      .sort((left, right) => {
        const leftDistance = Math.hypot(
          (left.bounds.minX + left.bounds.maxX) / 2 - viewport.center.x,
          (left.bounds.minZ + left.bounds.maxZ) / 2 - viewport.center.z,
        );
        const rightDistance = Math.hypot(
          (right.bounds.minX + right.bounds.maxX) / 2 - viewport.center.x,
          (right.bounds.minZ + right.bounds.maxZ) / 2 - viewport.center.z,
        );
        return leftDistance - rightDistance;
      })
      .slice(0, FULL_MAP_CONFIG.maxCachedTiles);
    fullMapDesiredIds = new Set(entries.map((entry) => entry.id));
    fullMapLoadQueue = [];
    fullMapQueuedIds.clear();
    for (const entry of entries) {
      const cached = fullMapTiles.get(entry.id);
      if (cached !== undefined) {
        fullMapLoadClock += 1;
        fullMapTiles.set(entry.id, {
          ...cached,
          lastUsed: fullMapLoadClock,
        });
        continue;
      }
      const minimapEntry = minimapTiles.get(entry.id);
      if (minimapEntry !== undefined) {
        cacheFullMapFeatures(minimapEntry.features);
        continue;
      }
      if (!fullMapActiveLoads.has(entry.id)) {
        fullMapLoadQueue.push(entry);
        fullMapQueuedIds.add(entry.id);
      }
    }
    for (const [tileId, controller] of fullMapActiveLoads) {
      if (!fullMapDesiredIds.has(tileId)) controller.abort();
    }
    pumpFullMapLoads();
  }

  function suspendFullMapLoading(): void {
    fullMapDesiredIds.clear();
    fullMapLoadQueue = [];
    fullMapQueuedIds.clear();
    for (const controller of fullMapActiveLoads.values()) {
      controller.abort();
    }
  }

  function enqueueTileAttachment(ready: Readonly<ReadyTileSnapshot>): void {
    renderQueueTail = renderQueueTail.then(async () => {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      const shouldContinue = (): boolean => {
        const current = streaming.getReadyTile(ready.tileId);
        return !disposed &&
          current?.payload === ready.payload &&
          current.lod === ready.lod;
      };
      if (!shouldContinue()) return;
      const renderMetrics = await renderer.attachWorldTileInPhases(
        ready.payload,
        { lod: ready.lod },
        shouldContinue,
      );
      if (renderMetrics === null) return;
      const elapsedMs = Object.values(renderMetrics.buildCpuMsByGroup)
        .reduce((total, durationMs) => total + durationMs, 0);
      if (elapsedMs > maximumTileAttachMs) {
        maximumTileAttachMs = elapsedMs;
        slowestAttachedTileId = ready.tileId;
        slowestTileBuildCpuMsByGroup = renderMetrics.buildCpuMsByGroup;
      }
    }).catch((error: unknown) => {
      streamingErrorCount += 1;
      console.error("Could not attach streamed tile", error);
    });
  }
  function enqueueMinimapExtraction(ready: Readonly<ReadyTileSnapshot>): void {
    minimapQueueTail = minimapQueueTail.then(async () => {
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      const current = streaming.getReadyTile(ready.tileId);
      if (
        current?.payload !== ready.payload ||
        current.lod !== ready.lod ||
        disposed
      ) {
        return;
      }
      if (minimapTiles.get(current.tileId)?.payload === current.payload) {
        return;
      }
      const startedAt = performance.now();
      const features = extractMinimapTileFeatures(
        current.payload,
        MINIMAP_CONFIG.lineSimplificationToleranceM,
      );
      lastMinimapExtractionMs = performance.now() - startedAt;
      maximumMinimapExtractionMs = Math.max(
        maximumMinimapExtractionMs,
        lastMinimapExtractionMs,
      );
      minimapTiles.set(current.tileId, {
        payload: current.payload,
        features,
      });
      minimapTileRevision += 1;
      cacheFullMapFeatures(features);
    }).catch((error: unknown) => {
      minimapExtractionErrorCount += 1;
      console.error("Could not prepare streamed tile for the local map", error);
    });
  }
  function synchronizePhysicsTile(
    ready: Readonly<ReadyTileSnapshot>,
  ): void {
    if (ready.lod === 2) {
      if (physics.hasTile(ready.tileId)) {
        physics.detachTile(ready.tileId);
      }
      physicsTilePayloads.delete(ready.tileId);
      return;
    }
    if (physicsTilePayloads.get(ready.tileId) === ready.payload) {
      return;
    }
    if (physics.hasTile(ready.tileId)) {
      physics.updateTile(ready.payload);
    } else {
      physics.attachTile(ready.payload);
    }
    physicsTilePayloads.set(ready.tileId, ready.payload);
  }
  const unsubscribeStreaming = streaming.subscribe((event) => {
    if (event.type === "error") {
      streamingErrorCount += 1;
      return;
    }
    if (event.type === "dispose") {
      // A stale response was never attached. Detaching by ID here could remove
      // a newer generation of the same tile.
      if (event.reason !== "stale-response") {
        renderer.detachWorldTile(event.tileId);
        physics.detachTile(event.tileId);
        physicsTilePayloads.delete(event.tileId);
      }
      if (
        (event.reason === "cache-eviction" ||
          event.reason === "controller-dispose") &&
        minimapTiles.get(event.tileId)?.payload === event.payload
      ) {
        minimapTiles.delete(event.tileId);
        minimapTileRevision += 1;
      }
      return;
    }
    if (event.to !== "ready") {
      return;
    }
    const ready = streaming.getReadyTile(event.tileId);
    if (ready !== undefined) {
      synchronizePhysicsTile(ready);
      // Build at most one tile view per event-loop task. This keeps geometry
      // preparation separate from the animation frame's shadowed draw.
      enqueueTileAttachment(ready);
      enqueueMinimapExtraction(ready);
    }
  });

  let lastTileRequestPosition: Readonly<{ x: number; z: number }> | null = null;
  let lastTileRequestWasFlying = false;
  let courtyardBookmark: Readonly<{
    x: number;
    z: number;
    facingYaw: number;
  }> | null = null;
  let state = stateAt({ ...STREET_BOOKMARK, y: 0 });
  let boatFleet = createInitialBoatFleetState();
  let roadVehicleFleet = (() => {
    const saved = localStorage.getItem(ROAD_VEHICLE_SAVE_KEY);
    if (saved === null) return createInitialRoadVehicleFleetState();
    try {
      return parseRoadVehicleFleet(saved);
    } catch {
      localStorage.removeItem(ROAD_VEHICLE_SAVE_KEY);
      return createInitialRoadVehicleFleetState();
    }
  })();
  let publicTransportFleet = (() => {
    const saved = localStorage.getItem(PUBLIC_TRANSPORT_SAVE_KEY);
    if (saved === null) return createInitialPublicTransportFleetState();
    try {
      return parsePublicTransportFleet(saved);
    } catch {
      localStorage.removeItem(PUBLIC_TRANSPORT_SAVE_KEY);
      return createInitialPublicTransportFleetState();
    }
  })();
  if (mountedPublicTransport(publicTransportFleet, LOCAL_PLAYER_ID) !== null) {
    roadVehicleFleet = clearRoadVehicleOccupancy(roadVehicleFleet);
  } else if (mountedRoadVehicle(roadVehicleFleet, LOCAL_PLAYER_ID) !== null) {
    publicTransportFleet = clearPublicTransportOccupancy(publicTransportFleet);
  }
  for (const spec of VILNIUS_ROAD_VEHICLES) {
    const vehicle = roadVehicleFleet.vehiclesById[spec.id];
    physics.attachVehicle({
      id: spec.id,
      position: vehicle?.position ?? spec.initialPosition,
      yawRadians: vehicle?.yawRadians ?? spec.initialYawRadians,
      pitchRadians: vehicle?.pitchRadians ?? 0,
      rollRadians: vehicle?.rollRadians ?? 0,
      bodySizeM: spec.bodySizeM,
    });
  }
  renderer.setBoatPresentations(boatPresentations(boatFleet));
  renderer.setRoadVehiclePresentations(roadVehiclePresentations(roadVehicleFleet));
  let activeScenario: RuntimeScenario | null = null;
  let instructionsVisible = true;
  let hasEngaged = false;
  let relocating = false;
  let disposed = false;
  let authoredAssetBackgroundTimer: ReturnType<typeof setTimeout> | null = null;
  const characterCapsule = Object.freeze({
    radiusM: MOVEMENT_TUNING.characterCapsuleRadiusMetres,
    halfHeightM:
      MOVEMENT_TUNING.characterCapsuleHeightMetres / 2 -
      MOVEMENT_TUNING.characterCapsuleRadiusMetres,
  });
  const browserRoadVehicleQueries: Readonly<RoadVehicleQueries> = {
    resolveMotion: (request) => {
      const resolution = physics.resolveVehicleMotion(request);
      const classification = resolution.groundSurface?.classification;
      const surfaceKind: RoadVehicleSurfaceKind = classification === undefined
        ? request.currentSurfaceKind
        : classification === "street"
        ? "road"
        : classification === "building"
        ? "hardscape"
        : "terrain";
      return {
        ...resolution,
        surfaceKind,
        surfaceId:
          resolution.groundSurface?.surfaceId ?? request.currentSurfaceId,
      };
    },
    resolveExit: (request) => physics.resolveVehicleExit({
      ...request,
      capsule: characterCapsule,
    }),
    recover: (request) => {
      physics.placeVehicle(
        request.vehicleId,
        request.position,
        request.yawRadians,
        0,
        0,
      );
    },
  };
  let synchronizeRoomTransfer: (
    position: Readonly<{ x: number; y: number; z: number }>,
    facingYaw: number,
  ) => Promise<boolean> = async () => false;
  const attachedPublicTransportColliderIds = new Set<string>();
  let currentPublicTransportPresentations:
    readonly PublicTransportVehiclePresentation[] = [];
  let currentPublicTransportColliderPresentations:
    readonly PublicTransportVehiclePresentation[] = [];
  let publicTransportRenderingReady = false;

  function terrainHeightForPublicTransport(
    position: Readonly<{ x: number; z: number }>,
    travelYawRadians?: number,
  ): number | null {
    const ready = streaming.getReadyTile(
      worldTileId(worldTileAddressAt(position)),
    );
    if (ready === undefined) return null;
    const bridge = sampleAuthoredBridgeSurface(
      publicTransportBridgeCollisions,
      position,
      travelYawRadians,
    );
    return bridge?.elevationM ?? sampleTerrainTileData(
      ready.payload.terrain,
      ready.payload.origin,
      position,
    );
  }

  const browserPublicTransportQueries: Readonly<PublicTransportQueries> = {
    groundHeight: terrainHeightForPublicTransport,
    resolveExit: (request) => physics.resolveVehicleExit({
      ...request,
      capsule: characterCapsule,
    }),
  };

  function synchronizePublicTransportColliders(
    focus: Readonly<{ x: number; z: number }>,
  ): void {
    const presentations = publicTransportColliderPresentations(
      publicTransportFleet,
      focus,
      terrainHeightForPublicTransport,
    );
    const desiredIds = new Set(presentations.map((vehicle) => vehicle.id));
    for (const id of [...attachedPublicTransportColliderIds]) {
      if (desiredIds.has(id)) continue;
      physics.detachVehicle(id);
      attachedPublicTransportColliderIds.delete(id);
    }
    for (const vehicle of presentations) {
      if (!attachedPublicTransportColliderIds.has(vehicle.id)) {
        physics.attachVehicle({
          id: vehicle.id,
          position: vehicle.position,
          yawRadians: vehicle.yawRadians,
          pitchRadians: 0,
          rollRadians: 0,
          bodySizeM: PUBLIC_TRANSPORT_VEHICLE_ASSETS[vehicle.mode].dimensionsM,
        });
        attachedPublicTransportColliderIds.add(vehicle.id);
      } else {
        physics.placeVehicle(
          vehicle.id,
          vehicle.position,
          vehicle.yawRadians,
          0,
          0,
        );
      }
    }
    currentPublicTransportColliderPresentations = presentations;
  }

  function synchronizePublicTransportPresentation(
    focus: Readonly<{ x: number; z: number }>,
  ): void {
    const player = state.playersById[LOCAL_PLAYER_ID];
    if (panelSampleActive || player?.locomotion.mode === "flying") {
      currentPublicTransportPresentations = [];
      if (publicTransportRenderingReady) {
        renderer.setPublicTransportPresentations([]);
      }
      for (const id of attachedPublicTransportColliderIds) {
        physics.detachVehicle(id);
      }
      attachedPublicTransportColliderIds.clear();
      currentPublicTransportColliderPresentations = [];
      return;
    }
    currentPublicTransportPresentations = publicTransportPresentations(
      publicTransportFleet,
      focus,
      terrainHeightForPublicTransport,
    );
    if (publicTransportRenderingReady) {
      renderer.setPublicTransportPresentations(
        currentPublicTransportPresentations,
      );
    }
    synchronizePublicTransportColliders(focus);
  }

  function requestTilesAt(
    position: Readonly<{ x: number; z: number }>,
    isFlying = false,
  ): void {
    lastTileRequestPosition = { x: position.x, z: position.z };
    lastTileRequestWasFlying = isFlying;
    streaming.update(
      position,
      streamingProfileFromViewSettings(
        worldViewSettings[isFlying ? "flight" : "grounded"],
      ),
    );
  }

  function applyWorldViewSettings(
    next: Readonly<WorldViewSettings>,
  ): void {
    const player = state.playersById[LOCAL_PLAYER_ID];
    const activeMode =
      player?.locomotion.mode === "flying" ||
          (player !== undefined && isTvTowerObservationPosition(player.position))
        ? "flight"
        : "grounded";
    const previousActiveProfile = worldViewSettings[activeMode];
    worldViewSettings = {
      schemaVersion: 1,
      grounded: { ...next.grounded },
      flight: { ...next.flight },
    };
    persistWorldViewSettings(localStorage, worldViewSettings);
    renderer.setVisibilitySettings(worldViewSettings);
    const nextActiveProfile = worldViewSettings[activeMode];
    const streamingEnvelopeChanged =
      nextActiveProfile.streamedRadiusTiles !==
        previousActiveProfile.streamedRadiusTiles ||
      nextActiveProfile.fullDetailRadiusM !==
        previousActiveProfile.fullDetailRadiusM;
    if (player !== undefined && streamingEnvelopeChanged) {
      requestTilesAt(
        player.position,
        player.locomotion.mode === "flying" ||
          isTvTowerObservationPosition(player.position),
      );
    }
  }

  function resetResidentPresentationStability(): void {
    activeNamedPresentations = [];
    activeAmbientNodeIds = [];
    activeAmbientRouteByNodeId.clear();
    residentTerrainHeightById.clear();
    lastResidentPresentations = [];
    renderer.setResidentPresentations([]);
  }

  function updateStreetLocationPresentation(
    position: Readonly<{ x: number; z: number }>,
  ): void {
    if (panelSampleActive) {
      activeStreetLocation = null;
      lastStreetLocationProbe = null;
      return;
    }
    const ready = streaming.getReadyTiles();
    const tileKey = ready.map((tile) => tile.tileId).sort().join("|");
    if (
      lastStreetLocationProbe !== null &&
      lastStreetLocationProbe.tileKey === tileKey &&
      Math.hypot(
        position.x - lastStreetLocationProbe.x,
        position.z - lastStreetLocationProbe.z,
      ) < STREET_LOCATION_CONFIG.sampleMovementM
    ) {
      return;
    }
    activeStreetLocation = updateStreetLocation(
      ready.map((tile) => tile.payload),
      position,
      activeStreetLocation,
      STREET_LOCATION_CONFIG,
    );
    lastStreetLocationProbe = { ...position, tileKey };
  }

  async function moveStateTo(
    position: Readonly<{ x: number; z: number }>,
    facingYaw = 0,
    preserveRoadVehicleMount = false,
    preservePublicTransportMount = false,
  ): Promise<void> {
    relocating = true;
    elevatorRide = createElevatorRideState();
    elevatorUi.update(null);
    try {
      const updateStartedAt = performance.now();
      requestTilesAt(position);
      lastUpdateMs = performance.now() - updateStartedAt;
      const settleStartedAt = performance.now();
      await streaming.settle();
      lastSettleMs = performance.now() - settleStartedAt;
      const renderQueueStartedAt = performance.now();
      await Promise.all([renderQueueTail, minimapQueueTail]);
      lastRenderQueueMs = performance.now() - renderQueueStartedAt;
      const metrics = streaming.metrics();
      if (metrics.errorTiles > 0) {
        throw new Error(
          `Could not settle streamed tiles: ${metrics.errorTiles} tile errors.`,
        );
      }
      const height = sampleReadyTerrain(streaming.getReadyTiles(), position);
      if (height === null) {
        throw new Error(
          `No streamed terrain is available at (${position.x}, ${position.z}).`,
        );
      }
      let nextState = withPlayerHeight(
        stateAt({ ...position, y: 0 }, facingYaw),
        height,
      );
      const currentRoomPlayer = state.playersById[LOCAL_PLAYER_ID];
      if (currentRoomPlayer !== undefined && Object.keys(state.playersById).length > 1) {
        const positioned = nextState.playersById[LOCAL_PLAYER_ID]!;
        nextState = {
          ...state,
          playersById: {
            ...state.playersById,
            [LOCAL_PLAYER_ID]: {
              ...currentRoomPlayer,
              position: { ...positioned.position },
              velocity: { x: 0, y: 0, z: 0 },
              facingYaw,
              lookYaw: facingYaw,
              isSprinting: false,
              locomotion: { ...positioned.locomotion },
            },
          },
        };
      }
      const restoredVehicleOverride = preserveRoadVehicleMount
        ? roadVehiclePlayerOverride(roadVehicleFleet, LOCAL_PLAYER_ID)
        : null;
      const restoredPublicTransportOverride = preservePublicTransportMount
        ? publicTransportPlayerOverride(
            publicTransportFleet,
            LOCAL_PLAYER_ID,
            terrainHeightForPublicTransport,
            height,
          )
        : null;
      if (restoredVehicleOverride !== null) {
        nextState = placePlayerInRoadVehicle(
          nextState,
          LOCAL_PLAYER_ID,
          restoredVehicleOverride.facingYaw,
          restoredVehicleOverride,
        );
      } else if (restoredPublicTransportOverride !== null) {
        nextState = applyPublicTransportPlayerTick(
          nextState,
          createNeutralInputIntent(
            LOCAL_PLAYER_ID,
            nextState.tick,
            restoredPublicTransportOverride.facingYaw,
          ),
          restoredPublicTransportOverride,
          0,
        );
      }
      const positionedPlayer = nextState.playersById[LOCAL_PLAYER_ID];
      if (positionedPlayer === undefined) {
        throw new Error(`Player ${LOCAL_PLAYER_ID} is missing from game state.`);
      }
      // Reposition the stable Rapier character instead of destroying and
      // recreating it after an asynchronous tile swap. Keeping all fallible
      // preparation above this point also leaves the previous playable state
      // intact when a transfer cannot settle.
      physics.placeCharacter(
        LOCAL_PLAYER_ID,
        positionedPlayer.position,
        characterCapsule,
      );
      if (
        restoredVehicleOverride !== null ||
        restoredPublicTransportOverride !== null
      ) {
        physics.detachCharacter(LOCAL_PLAYER_ID);
      }
      state = nextState;
      interiorPresence = createInteriorPresenceState();
      renderer.setInteriorPresentation({ activeInteriorId: null });
      input.resetLookYaw(facingYaw);
      lastStreetLocationProbe = null;
      resetResidentPresentationStability();
      boatFleet = createInitialBoatFleetState();
      renderer.setBoatPresentations(boatPresentations(boatFleet));
      if (!preserveRoadVehicleMount) {
        roadVehicleFleet = clearRoadVehicleOccupancy(roadVehicleFleet);
      }
      if (!preservePublicTransportMount) {
        publicTransportFleet = clearPublicTransportOccupancy(
          publicTransportFleet,
        );
      }
      for (const spec of VILNIUS_ROAD_VEHICLES) {
        const vehicle = roadVehicleFleet.vehiclesById[spec.id];
        physics.placeVehicle(
          spec.id,
          vehicle?.position ?? spec.initialPosition,
          vehicle?.yawRadians ?? spec.initialYawRadians,
          vehicle?.pitchRadians ?? 0,
          vehicle?.rollRadians ?? 0,
        );
      }
      localStorage.setItem(ROAD_VEHICLE_SAVE_KEY, serializeRoadVehicleFleet(roadVehicleFleet));
      localStorage.setItem(
        PUBLIC_TRANSPORT_SAVE_KEY,
        serializePublicTransportFleet(publicTransportFleet),
      );
      renderer.setRoadVehiclePresentations(roadVehiclePresentations(roadVehicleFleet));
      synchronizePublicTransportPresentation(positionedPlayer.position);
      lastContacts = [];
      lastSimulationEvents = [];
      renderer.setSnapshot(toRenderSnapshot(
        state,
        roadVehicleFleet,
        authoredInteriorById(interiorPresence.activeInteriorId)?.cameraDistanceM,
        publicTransportFleet,
      ), {
        resetInterpolation: true,
      });
      await synchronizeRoomTransfer(positionedPlayer.position, facingYaw);
    } finally {
      relocating = false;
    }
  }

  const startupRoadVehicle = mountedRoadVehicle(
    roadVehicleFleet,
    LOCAL_PLAYER_ID,
  );
  const startupPublicTransport = mountedPublicTransport(
    publicTransportFleet,
    LOCAL_PLAYER_ID,
  );
  await moveStateTo(
    startupPublicTransport?.presentation.position ??
      startupRoadVehicle?.vehicle.position ?? STREET_BOOKMARK,
    startupPublicTransport?.presentation.yawRadians ??
      startupRoadVehicle?.vehicle.yawRadians ??
      yawToward(STREET_BOOKMARK, STREET_ROUTE_NEXT),
    startupRoadVehicle !== null,
    startupPublicTransport !== null,
  );
  await renderer.prepareInitialShaderVariants();
  publicTransportRenderingReady = true;
  const startupPlayer = state.playersById[LOCAL_PLAYER_ID];
  if (startupPlayer !== undefined) {
    synchronizePublicTransportPresentation(startupPlayer.position);
  }
  courtyardBookmark = findCourtyardBookmark(streaming.getReadyTiles(), manifest);

  function resolveBookmark(
    bookmark: string,
  ): Readonly<{ x: number; z: number; facingYaw: number }> {
    return bookmark === "old-town-courtyard" &&
        courtyardBookmark !== null
      ? courtyardBookmark
      : bookmark === "three-crosses-vista"
        ? {
            ...VISTA_BOOKMARK,
            facingYaw: yawToward(
              VISTA_BOOKMARK,
              THREE_CROSSES_LANDMARK,
            ),
          }
        : {
            ...STREET_BOOKMARK,
            facingYaw: yawToward(STREET_BOOKMARK, STREET_ROUTE_NEXT),
          };
  }

  function nearestRiverContext(
    position: Readonly<{ x: number; z: number }>,
  ): Readonly<{
    nearestRiver: RiverIdentity | null;
    riverProximity01: number;
  }> {
    let nearest: { identity: RiverIdentity; distanceM: number } | null = null;
    for (const ready of streaming.getReadyTiles()) {
      for (const feature of ready.payload.waterLines) {
        const identity: RiverIdentity =
          feature.name?.toLocaleLowerCase("lt-LT").includes("ner")
            ? "neris"
            : "vilnia";
        for (const point of feature.points) {
          const distanceM = Math.hypot(
            ready.payload.origin.x + point.x - position.x,
            ready.payload.origin.z + point.z - position.z,
          );
          if (nearest === null || distanceM < nearest.distanceM) {
            nearest = { identity, distanceM };
          }
        }
      }
    }
    return nearest === null
      ? { nearestRiver: null, riverProximity01: 0 }
      : {
          nearestRiver: nearest.identity,
          riverProximity01: Math.max(0, 1 - nearest.distanceM / 120),
        };
  }

  renderer.setSnapshot(toRenderSnapshot(
    state,
    roadVehicleFleet,
    undefined,
    publicTransportFleet,
  ));

  function saveNeighborhoodState(): void {
    localStorage.setItem(
      NEIGHBORHOOD_SAVE_KEY,
      serializeNeighborhoodSave(neighborhood),
    );
  }

  function saveTraversalState(): void {
    localStorage.setItem(
      TRAVERSAL_SAVE_KEY,
      serializeTraversalSave(traversal),
    );
  }

  function showLockedTraversalGate(locked: Readonly<LockedTraversalGate>): void {
    traversalDiscoveryUi.show({
      label: locked.gate.label,
      connection: "",
      lockedMessage: locked.gate.lockedMessage,
    });
    traversalNoticeUntilSeconds =
      state.simulationTimeSeconds + TRAVERSAL_DISCOVERY_VISIBLE_SECONDS;
    lockedTraversalNoticeUntilSeconds = traversalNoticeUntilSeconds;
  }

  function openResidentDialogue(residentId: string): void {
    const closeAndRestoreGameFocus = (): void => {
      activeDialogueResidentId = null;
      dialogueUi.close();
      if (
        fullMap?.isOpen() !== true &&
        devMenu?.isOpen() !== true &&
        radioUi?.isOpen() !== true &&
        appearanceUi?.isOpen() !== true &&
        multiplayerUi?.isOpen() !== true
      ) {
        input.attach();
        canvas.focus({ preventScroll: true });
      }
    };
    activeDialogueResidentId = residentId;
    dialogueUi.open(
      dialogueForResident(neighborhood, residentId),
      (choiceId) => {
        if (choiceId === "leave") {
          closeAndRestoreGameFocus();
          return;
        }
        if (submitRoomCommand({
          kind: "neighborhood-action",
          action: choiceId as NeighborhoodAction,
        })) {
          closeAndRestoreGameFocus();
          return;
        }
        neighborhood = applyNeighborhoodAction(
          neighborhood,
          choiceId as NeighborhoodAction,
        );
        saveNeighborhoodState();
        synchronizeTraversalAccess();
        openResidentDialogue(residentId);
      },
    );
    void document.exitPointerLock?.();
  }

  function updateResidentPresentation(
    player: Readonly<{ x: number; z: number }>,
    hour: number,
    simulationTimeSeconds: number,
  ): void {
    const ready = streaming.getReadyTiles();
    const graphKey = ready.map((tile) => tile.tileId).sort().join("|");
    if (graphKey !== pedestrianGraphTileKey) {
      pedestrianGraphTileKey = graphKey;
      pedestrianGraph = buildPedestrianGraph(
        ready.map((tile) => tile.payload),
      );
    }
    const scheduledNamed = namedResidentPresentations(hour)
      .map((resident): ResidentPresentation => {
        if (resident.activity !== "walking") {
          return resident;
        }
        const pedestrianPoint = nearestPointOnPedestrianGraph(
          pedestrianGraph,
          resident.position,
          NEIGHBORHOOD_CONFIG.namedPedestrianSnapRadiusM,
        );
        return pedestrianPoint === null
          ? resident
          : {
              ...resident,
              position: {
                ...resident.position,
                x: pedestrianPoint.x,
                z: pedestrianPoint.z,
              },
            };
      })
      .flatMap((resident) => {
        const grounded = withTerrainHeight(
          resident,
          ready,
          residentTerrainHeightById,
        );
        return grounded === null ? [] : [grounded];
      });
    const named = stableNearbyResidentPresentations(
      scheduledNamed,
      activeNamedPresentations,
      player,
      NEIGHBORHOOD_CONFIG.namedSimulationRadiusM,
      NEIGHBORHOOD_CONFIG.namedDespawnRadiusM,
    );
    activeNamedPresentations = named;
    const ambientNodes = stableNearbyPedestrianNodes(
      pedestrianGraph,
      player,
      NEIGHBORHOOD_CONFIG.ambientSpawnRadiusM,
      NEIGHBORHOOD_CONFIG.ambientDespawnRadiusM,
      NEIGHBORHOOD_CONFIG.ambientPopulationCap,
      activeAmbientNodeIds,
    );
    activeAmbientNodeIds = ambientNodes.map((node) => node.id);
    const activeAmbientNodeIdSet = new Set(activeAmbientNodeIds);
    for (const nodeId of activeAmbientRouteByNodeId.keys()) {
      if (!activeAmbientNodeIdSet.has(nodeId)) {
        activeAmbientRouteByNodeId.delete(nodeId);
      }
    }
    const ambient = ambientNodes.flatMap((node): ResidentPresentation[] => {
      const identityHash = stableTextHash(node.id);
      let next = activeAmbientRouteByNodeId.get(node.id);
      if (!activeAmbientRouteByNodeId.has(node.id)) {
        const nextId = node.neighbours[
          identityHash % Math.max(1, node.neighbours.length)
        ];
        const nextNode = nextId === undefined
          ? undefined
          : pedestrianGraph.nodeById[nextId];
        next = nextNode === undefined
          ? null
          : { id: nextNode.id, x: nextNode.x, z: nextNode.z };
        activeAmbientRouteByNodeId.set(node.id, next);
      }
      const segmentLength = next === null || next === undefined
        ? 0
        : Math.hypot(next.x - node.x, next.z - node.z);
      const legSeconds = Math.max(
        1,
        segmentLength / NEIGHBORHOOD_CONFIG.ambientWalkSpeedMps,
      );
      const phaseOffset01 =
        stableTextHash(`${node.id}:phase`) / 0xffff_ffff;
      const phase = (
        simulationTimeSeconds + phaseOffset01 * legSeconds * 2
      ) % (legSeconds * 2);
      const returning = phase > legSeconds;
      const alpha = segmentLength === 0
        ? 0
        : returning
          ? 2 - phase / legSeconds
          : phase / legSeconds;
      const position = next === null || next === undefined
        ? { x: node.x, z: node.z }
        : {
            x: node.x + (next.x - node.x) * alpha,
            z: node.z + (next.z - node.z) * alpha,
          };
      if (
        authoredAmbientInteriors.some((interior) =>
          authoredInteriorExcludesAmbientPosition(interior, position)
        )
      ) {
        return [];
      }
      const id = `ambient:${node.id}`;
      const coatColour = identityHash % 2 === 0 ? 0x59635f : 0x63585c;
      const accentColour = identityHash % 3 === 0 ? 0xb0835f : 0x87969b;
      const sampledHeight = sampleReadyTerrain(ready, position);
      if (sampledHeight !== null) {
        residentTerrainHeightById.set(id, sampledHeight);
      }
      const height = sampledHeight ?? residentTerrainHeightById.get(id);
      if (height === undefined) {
        return [];
      }
      return [{
        id,
        name: "Passer-by",
        role: identityHash % 2 === 0
          ? "walking home"
          : "crossing the neighbourhood",
        position: {
          ...position,
          y: height,
        },
        yawRadians: next === null || next === undefined
          ? 0
          : Math.atan2(
              (next.x - node.x) * (returning ? -1 : 1),
              (next.z - node.z) * (returning ? -1 : 1),
            ),
        elderVariant: "none",
        clothing: {
          coat: coatColour,
          accent: accentColour,
        },
        appearance: generateCharacterAppearance(id, {
          colours: {
            outerwear: coatColour,
            accent: accentColour,
          },
        }),
        activity: "walking",
        named: false,
      }];
    });
    const venueOccupants: readonly ResidentPresentation[] =
      foodHallInterior !== null &&
        foodHallAmbientCentre !== null &&
        Math.hypot(
            foodHallAmbientCentre.x - player.x,
            foodHallAmbientCentre.z - player.z,
          ) <= NEIGHBORHOOD_CONFIG.authoredInteriorAmbientRadiusM
      ? (foodHallInterior.ambientOccupants ?? []).map((occupant) => ({
          id: `ambient:interior:${foodHallInterior.id}:${occupant.id}`,
          name: occupant.name,
          role: occupant.role,
          position: authoredInteriorWorldPoint(
            foodHallInterior,
            occupant.positionLocalM,
          ),
          yawRadians:
            occupant.yawRadians + foodHallInterior.rotationYRadians,
          elderVariant: "none",
          clothing: occupant.clothing,
          appearance: generateCharacterAppearance(
            `ambient:interior:${foodHallInterior.id}:${occupant.id}`,
            {
              colours: {
                outerwear: occupant.clothing.coat,
                accent: occupant.clothing.accent,
              },
            },
          ),
          activity: occupant.activity,
          named: false,
        }))
      : [];
    nearbyNamedResidentCount = named.length;
    ambientPopulationCount = ambient.length + venueOccupants.length;
    lastResidentPresentations = yieldResidentsToEmergencyVehicles(
      [...named, ...ambient, ...venueOccupants],
      roadVehiclePresentations(roadVehicleFleet),
    );
    renderer.setResidentPresentations(
      lastResidentPresentations.map((resident) => {
        if (resident.id !== activeDialogueResidentId) {
          return { ...resident, expression: "neutral" as const };
        }
        const positiveStanding =
          neighborhood.reputation.helpful +
          neighborhood.reputation.reliable +
          neighborhood.reputation.neighbourly;
        const waryStanding =
          neighborhood.reputation.nosy +
          neighborhood.reputation.show_off;
        return {
          ...resident,
          expression: positiveStanding > 0
            ? "warm" as const
            : neighborhood.reputation.odd > 0
              ? "concerned" as const
              : waryStanding > 0
                ? "wry" as const
                : "warm" as const,
        };
      }),
      state.simulationTimeSeconds,
    );
    const nearest = named
      .map((resident) => ({
        resident,
        distanceM: Math.hypot(
          resident.position.x - player.x,
          resident.position.z - player.z,
        ),
      }))
      .filter(
        (entry) =>
          entry.distanceM <= NEIGHBORHOOD_CONFIG.interactionRadiusM,
      )
      .sort((a, b) => a.distanceM - b.distanceM)[0];
    nearestInteractableResidentId = nearest?.resident.id ?? null;
    const nearestDefinition = nearestInteractableResidentId === null
      ? undefined
      : NAMED_RESIDENTS.find(
          (resident) => resident.id === nearestInteractableResidentId,
        );
    dialogueUi.setPrompt(
      nearestDefinition === undefined
        ? null
        : `Talk to ${nearestDefinition.name}`,
    );
    const destination = neighborhood.quest.status === "carrying"
      ? named.find(
          (resident) => resident.id === "ruta-kalniene",
        ) ?? scheduledNamed.find(
          (resident) => resident.id === "ruta-kalniene",
        )
      : undefined;
    if (destination === undefined) {
      dialogueUi.setObjective(null);
    } else {
      const deltaX = destination.position.x - player.x;
      const deltaZ = destination.position.z - player.z;
      dialogueUi.setObjective(
        `Key errand · Rūta Kalnienė · ${Math.round(Math.hypot(deltaX, deltaZ))} m ${compassDirection(deltaX, deltaZ)}`,
      );
    }
  }

  let fullMap: FullMap | null = null;
  let devMenu: DevMenu | null = null;
  let radioUi: RadioUi | null = null;
  let appearanceUi: AppearanceCustomizer | null = null;
  let unsubscribeRadio: (() => void) | null = null;
  let multiplayerUi: MultiplayerUi | null = null;
  let lastAppliedMultiplayerSnapshot: Readonly<MultiplayerRoomSnapshot> | null = null;
  let lastPresentedMultiplayerSnapshot: Readonly<MultiplayerRoomSnapshot> | null = null;
  let multiplayerSessionMode: "entry" | "solo" | "room" = "entry";
  let lastMultiplayerInputMoveX = 0;
  let lastMultiplayerInputMoveZ = 0;
  const multiplayerReconciliationCorrectionsMeters: number[] = [];
  let multiplayerMaximumReconciliationSample:
    VilniusRuntimeState["multiplayer"]["reconciliationMaximumSample"] = null;

  function remotePlayerPresentations(
    snapshot: Readonly<MultiplayerRoomSnapshot>,
    localPlayerId: string,
  ): readonly RemotePlayerPresentation[] {
    return Object.values(snapshot.game.playersById)
      .filter((player) => player.id !== localPlayerId)
      .map((player) => {
        const mounted = mountedRoadVehicle(snapshot.roadVehicles, player.id);
        const transit = mountedPublicTransport(
          snapshot.publicTransport,
          player.id,
        );
        return {
          player: {
            id: player.id,
            position: player.position,
            yawRadians: player.facingYaw,
            lookYawRadians: player.lookYaw,
            planarSpeedMetersPerSecond: Math.hypot(
              player.velocity.x,
              player.velocity.z,
            ),
            verticalSpeedMetersPerSecond: player.velocity.y,
            grounded: player.locomotion.grounded,
            isFlying: player.locomotion.mode === "flying",
            isSprinting: player.isSprinting,
            vehicleSeatRole:
              mounted?.seat.role ?? (transit === null ? null : "passenger"),
            mountedVehicleId:
              mounted?.vehicle.id ?? transit?.presentation.id ?? null,
            vehicleSteeringRadians: mounted?.vehicle.steeringRadians ?? 0,
          },
          appearance:
            snapshot.appearancesByPlayerId[player.id] ??
            generateCharacterAppearance(player.id),
        };
      });
  }

  function updateGameplayAudio(
    player: Readonly<GameState["playersById"][string]>,
    events: readonly Readonly<SimulationEvent>[],
  ): void {
    const activeInterior = authoredInteriorById(
      interiorPresence.activeInteriorId,
    );
    const environment = renderer.getEnvironmentState();
    const river = nearestRiverContext(player.position);
    const audioVehicle = mountedRoadVehicle(roadVehicleFleet, LOCAL_PLAYER_ID);
    lastAudioReport = audio.update(
      events,
      {
        tick: state.tick,
        dayIndex: 0,
        timeOfDayHours: environment.timeOfDayHours,
        season: environment.season,
        elevationMetres:
          player.position.y + manifest.localOriginLks94.elevationM,
        exposure01:
          activeScenario?.bookmark === "three-crosses-vista" ? 0.86 : 0.42,
        bellAudibility01: panelSampleActive ? 0.18 : 0.62,
        interiorProfile: activeInterior?.acousticProfile ?? null,
        interiorMix01: activeInterior === null ? 0 : 1,
        ...river,
      },
      audioVehicle === null
        ? null
        : {
            tick: state.tick,
            vehicleId: audioVehicle.vehicle.id,
            vehicleClass: audioVehicle.spec.vehicleClass,
            speedMps: audioVehicle.vehicle.speedMps,
            maximumForwardSpeedMps:
              ROAD_VEHICLE_TUNING.motionByClass[
                audioVehicle.spec.vehicleClass
              ].maximumForwardSpeedMps,
            longitudinalAccelerationMps2:
              audioVehicle.vehicle.longitudinalAccelerationMps2,
            surfaceKind: audioVehicle.vehicle.surfaceKind,
            wetness01: environment.surfaceWetness01,
            hornActive: audioVehicle.vehicle.hornActive,
            sirenActive: audioVehicle.vehicle.sirenOn,
            impactIntensity01: audioVehicle.vehicle.impactIntensity01,
          },
    );
    totalStartedAudioEventCount += lastAudioReport.startedEventIds.length;
  }

  function applyMultiplayerSnapshot(
    snapshot: Readonly<MultiplayerRoomSnapshot>,
    playerId: string,
    pendingIntents: readonly Readonly<import("../input/intents.ts").InputIntent>[] = [],
  ): void {
    const previousSnapshot = lastAppliedMultiplayerSnapshot;
    const previousPlayerId = LOCAL_PLAYER_ID;
    const initializesLocalLook = previousPlayerId !== playerId;
    if (previousPlayerId !== playerId) {
      physics.detachCharacter(previousPlayerId);
      LOCAL_PLAYER_ID = playerId;
    }
    state = structuredClone(snapshot.game);
    boatFleet = structuredClone(snapshot.boats);
    roadVehicleFleet = structuredClone(snapshot.roadVehicles);
    publicTransportFleet = structuredClone(snapshot.publicTransport);
    if (previousSnapshot?.neighborhood !== snapshot.neighborhood) {
      neighborhood = structuredClone(snapshot.neighborhood);
    }
    const nextTraversal = snapshot.traversalByPlayerId[playerId];
    if (
      previousSnapshot?.traversalByPlayerId[playerId] !== nextTraversal
    ) {
      traversal = structuredClone(nextTraversal ?? createTraversalState());
    }
    const nextInterior = snapshot.interiorByPlayerId[playerId];
    if (previousSnapshot?.interiorByPlayerId[playerId] !== nextInterior) {
      interiorPresence = structuredClone(
        nextInterior ?? createInteriorPresenceState(),
      );
    }
    const nextElevator = snapshot.elevatorByPlayerId[playerId];
    if (previousSnapshot?.elevatorByPlayerId[playerId] !== nextElevator) {
      elevatorRide = structuredClone(nextElevator ?? createElevatorRideState());
    }
    let localPlayer = state.playersById[playerId];
    if (localPlayer === undefined) return;
    const appearance = snapshot.appearancesByPlayerId[playerId];
    if (
      appearance !== undefined &&
      !sameCharacterAppearance(playerAppearance, appearance)
    ) {
      playerAppearance = structuredClone(appearance);
      renderer.setPlayerAppearance(playerAppearance);
    }
    for (const spec of VILNIUS_ROAD_VEHICLES) {
      const vehicle = roadVehicleFleet.vehiclesById[spec.id];
      const previousVehicle = previousSnapshot?.roadVehicles.vehiclesById[spec.id];
      if (
        vehicle !== undefined &&
        (
          previousVehicle === undefined ||
          vehicle.position.x !== previousVehicle.position.x ||
          vehicle.position.y !== previousVehicle.position.y ||
          vehicle.position.z !== previousVehicle.position.z ||
          vehicle.yawRadians !== previousVehicle.yawRadians ||
          vehicle.pitchRadians !== previousVehicle.pitchRadians ||
          vehicle.rollRadians !== previousVehicle.rollRadians
        )
      ) {
        physics.placeVehicle(
          vehicle.id,
          vehicle.position,
          vehicle.yawRadians,
          vehicle.pitchRadians,
          vehicle.rollRadians,
        );
      }
    }
    const inVehicle = mountedRoadVehicle(roadVehicleFleet, playerId) !== null;
    const onPublicTransport =
      mountedPublicTransport(publicTransportFleet, playerId) !== null;
    const onBoat = mountedBoatId(boatFleet, playerId) !== null;
    if (
      inVehicle ||
      onPublicTransport ||
      onBoat ||
      elevatorRide.phase === "travelling"
    ) {
      physics.detachCharacter(playerId);
    } else {
      physics.placeCharacter(playerId, localPlayer.position, characterCapsule);
    }
    const presentedSnapshot = pendingIntents.length === 0
      ? snapshot
      : predictMultiplayerRoomSnapshot(snapshot, {
          playerId,
          pendingIntents,
          publicTransportQueries: browserPublicTransportQueries,
          roadVehicleQueries: browserRoadVehicleQueries,
          resolveLocalCharacterMotion: (request) => {
            // Replaying an input horizon can finish an authored mount/lift
            // transition that began while the browser capsule was detached.
            // Re-place at the request's authoritative start before the first
            // walking resolution in that same reconciliation pass.
            physics.placeCharacter(
              request.playerId,
              request.currentPosition,
              request.capsule,
            );
            return physics.resolveCharacterMotion(request);
          },
        });
    const previouslyPresentedPlayer =
      lastPresentedMultiplayerSnapshot?.game.playersById[playerId];
    const nextPresentedPlayer = presentedSnapshot.game.playersById[playerId];
    if (
      !relocating &&
      previouslyPresentedPlayer !== undefined &&
      nextPresentedPlayer !== undefined
    ) {
      const correctionMeters = Math.hypot(
        nextPresentedPlayer.position.x - previouslyPresentedPlayer.position.x,
        nextPresentedPlayer.position.y - previouslyPresentedPlayer.position.y,
        nextPresentedPlayer.position.z - previouslyPresentedPlayer.position.z,
      );
      multiplayerReconciliationCorrectionsMeters.push(correctionMeters);
      if (
        multiplayerMaximumReconciliationSample === null ||
        correctionMeters >
          multiplayerMaximumReconciliationSample.distanceMeters
      ) {
        multiplayerMaximumReconciliationSample = {
          distanceMeters: correctionMeters,
          authoritativeTick: snapshot.game.tick,
          presentedTick: presentedSnapshot.game.tick,
          pendingIntentCount: pendingIntents.length,
          from: { ...previouslyPresentedPlayer.position },
          to: { ...nextPresentedPlayer.position },
        };
      }
      if (multiplayerReconciliationCorrectionsMeters.length > 1_200) {
        multiplayerReconciliationCorrectionsMeters.shift();
      }
    }
    if (presentedSnapshot !== snapshot) {
      state = structuredClone(presentedSnapshot.game);
      boatFleet = structuredClone(presentedSnapshot.boats);
      roadVehicleFleet = structuredClone(presentedSnapshot.roadVehicles);
      publicTransportFleet = structuredClone(presentedSnapshot.publicTransport);
      traversal = structuredClone(
        presentedSnapshot.traversalByPlayerId[playerId] ?? createTraversalState(),
      );
      interiorPresence = structuredClone(
        presentedSnapshot.interiorByPlayerId[playerId] ?? createInteriorPresenceState(),
      );
      elevatorRide = structuredClone(
        presentedSnapshot.elevatorByPlayerId[playerId] ?? createElevatorRideState(),
      );
      localPlayer = state.playersById[playerId];
      if (localPlayer === undefined) return;
      for (const vehicle of Object.values(roadVehicleFleet.vehiclesById)) {
        physics.placeVehicle(
          vehicle.id,
          vehicle.position,
          vehicle.yawRadians,
          vehicle.pitchRadians,
          vehicle.rollRadians,
        );
      }
      if (
        mountedRoadVehicle(roadVehicleFleet, playerId) !== null ||
        mountedPublicTransport(publicTransportFleet, playerId) !== null ||
        mountedBoatId(boatFleet, playerId) !== null ||
        elevatorRide.phase === "travelling"
      ) {
        physics.detachCharacter(playerId);
      } else {
        physics.placeCharacter(playerId, localPlayer.position, characterCapsule);
      }
    }
    if (initializesLocalLook) input.resetLookYaw(localPlayer.lookYaw);
    renderer.setEnvironment(snapshot.environment);
    if (
      previousSnapshot?.interiorByPlayerId[playerId]?.activeInteriorId !==
        interiorPresence.activeInteriorId
    ) {
      renderer.setInteriorPresentation({
        activeInteriorId: interiorPresence.activeInteriorId,
      });
    }
    renderer.setBoatPresentations(boatPresentations(boatFleet));
    renderer.setRoadVehiclePresentations(
      roadVehiclePresentations(roadVehicleFleet),
    );
    synchronizePublicTransportPresentation(localPlayer.position);
    const residentSeed = residentSeedForAppliedRoomSnapshot(
      snapshot.residents,
      lastResidentPresentations,
    );
    if (residentSeed !== null) {
      renderer.setResidentPresentations(
        residentSeed,
        snapshot.game.simulationTimeSeconds,
      );
    }
    renderer.setRemotePlayerPresentations(
      remotePlayerPresentations(snapshot, playerId),
    );
    renderer.setSnapshot(toRenderSnapshot(
      state,
      roadVehicleFleet,
      authoredInteriorById(interiorPresence.activeInteriorId)?.cameraDistanceM,
      publicTransportFleet,
    ), {
      verticalCorrectionSmoothing: {
        responsePerSecond:
          MULTIPLAYER_CONFIG.localVerticalReconciliationResponsePerSecond,
        maximumDistanceMeters:
          MULTIPLAYER_CONFIG.localVerticalReconciliationMaximumSmoothedDistanceMeters,
      },
    });
    renderer.setLocalLookYaw(input.lookYaw());
    elevatorUi.update(elevatorRidePresentation(elevatorRide));
    synchronizeCameraCollision(
      localPlayer.locomotion.mode,
      mountedBoatId(boatFleet, playerId) !== null,
      interiorPresence.activeInteriorId !== null,
    );
    const mountedTransit = mountedPublicTransport(
      publicTransportFleet,
      playerId,
    );
    cameraExcludedRoadVehicleColliderIds = [
      ...(mountedRoadVehicle(roadVehicleFleet, playerId) === null
        ? []
        : VILNIUS_ROAD_VEHICLES.map((vehicle) => `vehicle:${vehicle.id}`)),
      ...(mountedTransit === null
        ? []
        : [`vehicle:${mountedTransit.presentation.id}`]),
    ];
    lastSimulationEvents = [...presentedSnapshot.semanticEvents];
    updateGameplayAudio(localPlayer, presentedSnapshot.semanticEvents);
    const previousTraversalIds = new Set(
      previousSnapshot?.traversalByPlayerId[playerId]?.discoveredSiteIds ?? [],
    );
    const newlyDiscovered = traversal.discoveredSiteIds
      .filter((id) => !previousTraversalIds.has(id))
      .map((id) => TRAVERSAL_DISCOVERY_SITES.find((site) => site.id === id))
      .find((site) => site !== undefined);
    if (
      newlyDiscovered !== undefined &&
      state.simulationTimeSeconds >= lockedTraversalNoticeUntilSeconds
    ) {
      traversalDiscoveryUi.show(newlyDiscovered);
      traversalNoticeUntilSeconds =
        state.simulationTimeSeconds + TRAVERSAL_DISCOVERY_VISIBLE_SECONDS;
    } else if (
      traversalNoticeUntilSeconds > 0 &&
      state.simulationTimeSeconds >= traversalNoticeUntilSeconds
    ) {
      traversalNoticeUntilSeconds = 0;
      lockedTraversalNoticeUntilSeconds = 0;
      traversalDiscoveryUi.hide();
    }
    if (previousSnapshot?.neighborhood.access !== snapshot.neighborhood.access) {
      synchronizeTraversalAccess();
    }
    const usesAerialVisibility =
      localPlayer.locomotion.mode === "flying" ||
      isTvTowerObservationPosition(localPlayer.position);
    if (
      !relocating &&
      (
        usesAerialVisibility !== lastTileRequestWasFlying ||
        shouldRefreshTileSelection(
          lastTileRequestPosition,
          localPlayer.position,
          manifest.tileSizeM,
          TILE_STREAMING_LOD_REFRESH_DISTANCE_M,
        )
      )
    ) {
      requestTilesAt(localPlayer.position, usesAerialVisibility);
    }
    lastAppliedMultiplayerSnapshot = snapshot;
    lastPresentedMultiplayerSnapshot = presentedSnapshot;
    multiplayerUi?.setRoomSnapshot(snapshot, playerId);
  }

  function advanceLocalMultiplayerPrediction(
    intent: Readonly<import("../input/intents.ts").InputIntent>,
  ): void {
    const current = lastPresentedMultiplayerSnapshot;
    if (current === null) return;
    const predicted = predictMultiplayerRoomSnapshot(current, {
      playerId: LOCAL_PLAYER_ID,
      pendingIntents: [intent],
      publicTransportQueries: browserPublicTransportQueries,
      roadVehicleQueries: browserRoadVehicleQueries,
      resolveLocalCharacterMotion: (request) => {
        physics.placeCharacter(
          request.playerId,
          request.currentPosition,
          request.capsule,
        );
        return physics.resolveCharacterMotion(request);
      },
    });
    lastPresentedMultiplayerSnapshot = predicted;
    state = structuredClone(predicted.game);
    boatFleet = structuredClone(predicted.boats);
    roadVehicleFleet = structuredClone(predicted.roadVehicles);
    publicTransportFleet = structuredClone(predicted.publicTransport);
    traversal = structuredClone(
      predicted.traversalByPlayerId[LOCAL_PLAYER_ID] ?? createTraversalState(),
    );
    const previousInteriorId = interiorPresence.activeInteriorId;
    interiorPresence = structuredClone(
      predicted.interiorByPlayerId[LOCAL_PLAYER_ID] ?? createInteriorPresenceState(),
    );
    elevatorRide = structuredClone(
      predicted.elevatorByPlayerId[LOCAL_PLAYER_ID] ?? createElevatorRideState(),
    );
    const player = state.playersById[LOCAL_PLAYER_ID];
    if (player === undefined) return;
    if (
      mountedRoadVehicle(roadVehicleFleet, LOCAL_PLAYER_ID) !== null ||
      mountedPublicTransport(publicTransportFleet, LOCAL_PLAYER_ID) !== null ||
      mountedBoatId(boatFleet, LOCAL_PLAYER_ID) !== null ||
      elevatorRide.phase === "travelling"
    ) {
      physics.detachCharacter(LOCAL_PLAYER_ID);
    } else {
      physics.placeCharacter(LOCAL_PLAYER_ID, player.position, characterCapsule);
    }
    if (previousInteriorId !== interiorPresence.activeInteriorId) {
      renderer.setInteriorPresentation({
        activeInteriorId: interiorPresence.activeInteriorId,
      });
    }
    renderer.setBoatPresentations(boatPresentations(boatFleet));
    renderer.setRoadVehiclePresentations(
      roadVehiclePresentations(roadVehicleFleet),
    );
    synchronizePublicTransportPresentation(player.position);
    renderer.setSnapshot(toRenderSnapshot(
      state,
      roadVehicleFleet,
      authoredInteriorById(interiorPresence.activeInteriorId)?.cameraDistanceM,
      publicTransportFleet,
    ));
    elevatorUi.update(elevatorRidePresentation(elevatorRide));
    synchronizeCameraCollision(
      player.locomotion.mode,
      mountedBoatId(boatFleet, LOCAL_PLAYER_ID) !== null,
      interiorPresence.activeInteriorId !== null,
    );
    const mountedTransit = mountedPublicTransport(
      publicTransportFleet,
      LOCAL_PLAYER_ID,
    );
    cameraExcludedRoadVehicleColliderIds = [
      ...(mountedRoadVehicle(roadVehicleFleet, LOCAL_PLAYER_ID) === null
        ? []
        : VILNIUS_ROAD_VEHICLES.map((vehicle) => `vehicle:${vehicle.id}`)),
      ...(mountedTransit === null
        ? []
        : [`vehicle:${mountedTransit.presentation.id}`]),
    ];
    lastSimulationEvents = [...predicted.semanticEvents];
    updateGameplayAudio(player, predicted.semanticEvents);
    const usesAerialVisibility =
      player.locomotion.mode === "flying" ||
      isTvTowerObservationPosition(player.position);
    if (
      usesAerialVisibility !== lastTileRequestWasFlying ||
      shouldRefreshTileSelection(
        lastTileRequestPosition,
        player.position,
        manifest.tileSizeM,
        TILE_STREAMING_LOD_REFRESH_DISTANCE_M,
      )
    ) {
      requestTilesAt(player.position, usesAerialVisibility);
    }
  }

  const multiplayer = new MultiplayerClient({
    onStatus(status, detail): void {
      multiplayerUi?.setStatus(status, detail);
    },
    onWelcome(value): void {
      sessionStorage.removeItem(MULTIPLAYER_SOLO_SESSION_KEY);
      multiplayerSessionMode = "room";
      // The local player and occupied vehicles are predicted at the fixed
      // 60 Hz simulation rate, so they use the loop's interpolation alpha.
      // Remote players own a separate packet-time interpolation buffer.
      applyMultiplayerSnapshot(value.snapshot, value.playerId);
      multiplayerUi?.setChatHistory(value.chatHistory);
      multiplayerUi?.enterRoom();
      // Entering a room replaces the modal lobby with the game. Re-arm input
      // explicitly so a hidden name/password field can never retain keyboard
      // ownership after the welcome packet closes the gate.
      input.attach();
      instructionsVisible = true;
      canvas.focus({ preventScroll: true });
      if (authoredAssetBackgroundTimer !== null) {
        clearTimeout(authoredAssetBackgroundTimer);
      }
      authoredAssetBackgroundTimer = setTimeout(() => {
        authoredAssetBackgroundTimer = null;
        if (!disposed && multiplayer.connectionStatus === "connected") {
          renderer.startDeferredAuthoredAssetStreaming();
        }
      }, VILNIUS_AUTHORED_ASSET_BACKGROUND_START_DELAY_MS);
    },
    onSnapshot(snapshot, pendingIntents): void {
      const playerId = multiplayer.connectedPlayerId;
      if (playerId !== null) {
        applyMultiplayerSnapshot(snapshot, playerId, pendingIntents);
      }
    },
    onChat(message): void {
      multiplayerUi?.appendChat(message);
    },
    onPresence(playerId, displayName, connected): void {
      multiplayerUi?.appendPresence(
        playerId,
        displayName,
        connected,
      );
    },
    onError(message): void {
      multiplayerUi?.showError(message);
    },
  });

  synchronizeRoomTransfer = async (position, facingYaw) => {
    if (multiplayer.connectionStatus !== "connected") return false;
    await multiplayer.sendCommand({
      kind: "transfer-player",
      position,
      facingYaw,
    });
    return true;
  };

  function submitRoomCommand(command: Readonly<RoomCommand>): boolean {
    if (multiplayer.connectionStatus !== "connected") return false;
    void multiplayer.sendCommand(command).catch((reason) => {
      multiplayerUi?.showError(
        reason instanceof Error ? reason.message : "The room rejected that change.",
      );
    });
    return true;
  }

  multiplayerUi = createMultiplayerUi(hudRoot, {
    onOpenChange(open): void {
      if (open) {
        fullMap?.close();
        appearanceUi?.close();
        devMenu?.close();
        radioUi?.close();
        input.detach();
        input.resetTouch();
        instructionsVisible = false;
        if (document.pointerLockElement !== null) document.exitPointerLock();
      } else if (
        !disposed &&
        fullMap?.isOpen() !== true &&
        devMenu?.isOpen() !== true &&
        radioUi?.isOpen() !== true &&
        appearanceUi?.isOpen() !== true
      ) {
        input.attach();
        instructionsVisible = true;
        canvas.focus({ preventScroll: true });
      }
    },
    onRefresh: () => multiplayer.listRooms(),
    async onCreate(value): Promise<void> {
      await multiplayer.createRoom({
        roomName: value.roomName,
        displayName: value.displayName,
        ...(value.password === "" ? {} : { password: value.password }),
        appearance: playerAppearance,
      });
    },
    async onJoin(value): Promise<void> {
      await multiplayer.joinRoom(value.roomId, {
        displayName: value.displayName,
        ...(value.password === "" ? {} : { password: value.password }),
        appearance: playerAppearance,
      });
    },
    onSolo(): void {
      sessionStorage.setItem(MULTIPLAYER_SOLO_SESSION_KEY, "1");
      multiplayerSessionMode = "solo";
      lastAppliedMultiplayerSnapshot = null;
      lastPresentedMultiplayerSnapshot = null;
      multiplayerReconciliationCorrectionsMeters.length = 0;
      multiplayerUi?.enterSolo();
      input.attach();
      instructionsVisible = true;
      canvas.focus({ preventScroll: true });
      if (authoredAssetBackgroundTimer !== null) {
        clearTimeout(authoredAssetBackgroundTimer);
      }
      authoredAssetBackgroundTimer = setTimeout(() => {
        authoredAssetBackgroundTimer = null;
        if (!disposed && multiplayer.connectionStatus === "offline") {
          renderer.startDeferredAuthoredAssetStreaming();
        }
      }, VILNIUS_AUTHORED_ASSET_BACKGROUND_START_DELAY_MS);
    },
    onChat(text): void {
      multiplayer.sendChat(text);
    },
    onLeave(): void {
      sessionStorage.removeItem(MULTIPLAYER_SOLO_SESSION_KEY);
      multiplayer.disconnect();
      window.location.reload();
    },
  });
  if (sessionStorage.getItem(MULTIPLAYER_SOLO_SESSION_KEY) === "1") {
    multiplayerSessionMode = "solo";
    multiplayerUi.enterSolo();
    input.attach();
    instructionsVisible = true;
    canvas.focus({ preventScroll: true });
    if (authoredAssetBackgroundTimer !== null) {
      clearTimeout(authoredAssetBackgroundTimer);
    }
    authoredAssetBackgroundTimer = setTimeout(() => {
      authoredAssetBackgroundTimer = null;
      if (!disposed && multiplayer.connectionStatus === "offline") {
        renderer.startDeferredAuthoredAssetStreaming();
      }
    }, VILNIUS_AUTHORED_ASSET_BACKGROUND_START_DELAY_MS);
  } else {
    void multiplayer.resumeStoredSession();
  }

  const loop = createGameLoop({
    step(fixedDeltaSeconds): void {
      if (relocating) {
        return;
      }
      if (
        multiplayer.connectionStatus === "connected" ||
        multiplayer.connectionStatus === "reconnecting"
      ) {
        if (multiplayer.connectionStatus === "connected") {
          if (
            fullMap?.isOpen() !== true &&
            devMenu?.isOpen() !== true &&
            radioUi?.isOpen() !== true &&
            appearanceUi?.isOpen() !== true &&
            multiplayerUi?.isOpen() !== true &&
            !dialogueUi.isOpen() &&
            elevatorRide.phase !== "travelling"
          ) {
            // Overlay close callbacks normally re-arm keyboard input. Keep the
            // connected simulation self-healing as well: focus can move to a
            // removed dialogue/map control between ticks in real browsers.
            input.attach();
          }
          const sampledIntent = input.sampleIntent(
            LOCAL_PLAYER_ID,
            multiplayer.allocateInputTick(),
          );
          lastMultiplayerInputMoveX = sampledIntent.moveX;
          lastMultiplayerInputMoveZ = sampledIntent.moveZ;
          const player = state.playersById[LOCAL_PLAYER_ID];
          const worldInteractionAvailable = player !== undefined && (
            elevatorInteractionPrompt(elevatorRide, player.position) !== null ||
            roadVehicleInteractionPrompt(
              roadVehicleFleet,
              LOCAL_PLAYER_ID,
              player.position,
            ) !== null ||
            boatInteractionPrompt(
              boatFleet,
              LOCAL_PLAYER_ID,
              player.position,
            ) !== null ||
            publicTransportInteractionPrompt(
              publicTransportFleet,
              LOCAL_PLAYER_ID,
              player.position,
              terrainHeightForPublicTransport,
            ) !== null
          );
          let consumesLocalInteraction = false;
          if (
            sampledIntent.interactPressed &&
            !worldInteractionAvailable &&
            !dialogueUi.isOpen()
          ) {
            if (nearestLockedGate !== null) {
              showLockedTraversalGate(nearestLockedGate);
              consumesLocalInteraction = true;
            } else if (nearestInteractableResidentId !== null) {
              openResidentDialogue(nearestInteractableResidentId);
              consumesLocalInteraction = true;
            }
          }
          multiplayer.sendIntent(
            consumesLocalInteraction
              ? { ...sampledIntent, interactPressed: false }
              : sampledIntent,
          );
          advanceLocalMultiplayerPrediction(
            consumesLocalInteraction
              ? { ...sampledIntent, interactPressed: false }
              : sampledIntent,
          );
        }
        return;
      }
      const intent = input.sampleIntent(LOCAL_PLAYER_ID, state.tick + 1);
      const playerBefore = state.playersById[LOCAL_PLAYER_ID];
      if (playerBefore === undefined) {
        return;
      }
      const elevatorResult = stepElevatorRide(
        elevatorRide,
        intent,
        playerBefore.position,
        playerBefore.facingYaw,
        fixedDeltaSeconds,
      );
      elevatorRide = elevatorResult.state;
      elevatorUi.update(elevatorResult.presentation);
      if (elevatorResult.transition === "departed") {
        physics.detachCharacter(LOCAL_PLAYER_ID);
      }
      if (
        elevatorResult.transition === "transferred" &&
        elevatorResult.playerOverride !== null
      ) {
        input.resetLookYaw(elevatorResult.playerOverride.facingYaw);
      }
      const worldIntent = elevatorResult.interactionConsumed
        ? { ...intent, interactPressed: false }
        : intent;
      const transitCanConsumeInteraction =
        mountedRoadVehicle(roadVehicleFleet, LOCAL_PLAYER_ID) === null &&
        mountedBoatId(boatFleet, LOCAL_PLAYER_ID) === null;
      const publicTransportResult = stepPublicTransportFleet(
        publicTransportFleet,
        transitCanConsumeInteraction
          ? worldIntent
          : { ...worldIntent, interactPressed: false },
        playerBefore.position,
        fixedDeltaSeconds,
        {
          groundHeight: terrainHeightForPublicTransport,
          resolveExit: (request) => physics.resolveVehicleExit({
            ...request,
            capsule: characterCapsule,
          }),
        },
      );
      publicTransportFleet = publicTransportResult.state;
      if (publicTransportResult.blockedExit) {
        vehicleExitBlockedUntilSeconds = state.simulationTimeSeconds + 2.5;
      }
      if (publicTransportResult.transition === "boarded") {
        physics.detachCharacter(LOCAL_PLAYER_ID);
      }
      const roadIntent =
        publicTransportResult.interactionConsumed ||
          publicTransportResult.playerOverride !== null
          ? { ...worldIntent, interactPressed: false }
          : worldIntent;
      const vehicleResult = stepRoadVehicleFleet(
        roadVehicleFleet,
        roadIntent,
        playerBefore.position,
        fixedDeltaSeconds,
        browserRoadVehicleQueries,
        { wetness01: renderer.getEnvironmentState().surfaceWetness01 },
      );
      roadVehicleFleet = vehicleResult.state;
      if (vehicleResult.blockedExit) {
        vehicleExitBlockedUntilSeconds = state.simulationTimeSeconds + 2.5;
      }
      if (vehicleResult.transition === "entered") {
        physics.detachCharacter(LOCAL_PLAYER_ID);
      }
      const boatResult = vehicleResult.playerOverride === null
          && publicTransportResult.playerOverride === null
        ? stepBoatFleet(
            boatFleet,
            vehicleResult.interactionConsumed
              ? { ...roadIntent, interactPressed: false }
              : roadIntent,
            playerBefore.position,
            fixedDeltaSeconds,
          )
        : {
            state: boatFleet,
            transition: null,
            interactionConsumed: vehicleResult.interactionConsumed,
            playerOverride: null,
          };
      boatFleet = boatResult.state;
      if (boatResult.transition === "boarded") {
        physics.detachCharacter(LOCAL_PLAYER_ID);
      }
      if (elevatorResult.playerOverride !== null) {
        state = applyElevatorPlayerTick(
          state,
          LOCAL_PLAYER_ID,
          intent.tick,
          elevatorResult.playerOverride,
          fixedDeltaSeconds,
        );
        lastContacts = [];
        lastSimulationEvents = [];
        physics.step(fixedDeltaSeconds);
        if (elevatorResult.transition === "arrived") {
          physics.attachCharacter(
            LOCAL_PLAYER_ID,
            elevatorResult.playerOverride.position,
            characterCapsule,
          );
        }
      } else if (publicTransportResult.playerOverride !== null) {
        state = applyPublicTransportPlayerTick(
          state,
          intent,
          publicTransportResult.playerOverride,
          fixedDeltaSeconds,
        );
        lastContacts = [];
        lastSimulationEvents = [];
        physics.step(fixedDeltaSeconds);
        if (
          publicTransportResult.transition === "alighted" ||
          publicTransportResult.transition === "terminal-alighted"
        ) {
          physics.attachCharacter(
            LOCAL_PLAYER_ID,
            publicTransportResult.playerOverride.position,
            characterCapsule,
          );
        }
      } else if (vehicleResult.playerOverride !== null) {
        state = applyRoadVehiclePlayerTick(
          state,
          intent,
          vehicleResult.playerOverride,
          fixedDeltaSeconds,
        );
        lastContacts = [];
        lastSimulationEvents = [];
        physics.step(fixedDeltaSeconds);
        if (vehicleResult.transition === "exited") {
          physics.attachCharacter(
            LOCAL_PLAYER_ID,
            vehicleResult.playerOverride.position,
            characterCapsule,
          );
        }
        localStorage.setItem(
          ROAD_VEHICLE_SAVE_KEY,
          serializeRoadVehicleFleet(roadVehicleFleet),
        );
      } else if (boatResult.playerOverride !== null) {
        state = applyBoatPlayerTick(
          state,
          intent,
          boatResult.playerOverride,
          fixedDeltaSeconds,
        );
        lastContacts = [];
        lastSimulationEvents = [];
        physics.step(fixedDeltaSeconds);
        if (boatResult.transition === "dismounted") {
          physics.attachCharacter(
            LOCAL_PLAYER_ID,
            boatResult.playerOverride.position,
            characterCapsule,
          );
        }
      } else {
        const lockedGateInteraction =
          !elevatorResult.interactionConsumed &&
          !publicTransportResult.interactionConsumed &&
          !boatResult.interactionConsumed && !vehicleResult.interactionConsumed &&
          intent.interactPressed &&
          nearestLockedGate !== null &&
          !dialogueUi.isOpen();
        if (lockedGateInteraction && nearestLockedGate !== null) {
          showLockedTraversalGate(nearestLockedGate);
        }
        if (
          !elevatorResult.interactionConsumed &&
          !publicTransportResult.interactionConsumed &&
          !boatResult.interactionConsumed && !vehicleResult.interactionConsumed &&
          !lockedGateInteraction &&
          intent.interactPressed &&
          nearestInteractableResidentId !== null &&
          !dialogueUi.isOpen()
        ) {
          openResidentDialogue(nearestInteractableResidentId);
        }
        const plan = planGameTick(state, [intent], fixedDeltaSeconds);
        const resolutions = plan.motionRequests.map((request) =>
          physics.resolveCharacterMotion(request)
        );
        physics.step(fixedDeltaSeconds);
        const result = completeGameTick(state, plan, resolutions);
        state = result.state;
        lastSimulationEvents = result.events;
        lastContacts = resolutions.flatMap(
          (resolution) => [...resolution.contacts],
        );
      }
      renderer.setBoatPresentations(boatPresentations(boatFleet));
      renderer.setRoadVehiclePresentations(
        roadVehiclePresentations(roadVehicleFleet),
      );
      const playerAfterSimulation = state.playersById[LOCAL_PLAYER_ID];
      if (playerAfterSimulation !== undefined) {
        synchronizePublicTransportPresentation(playerAfterSimulation.position);
      }
      if (
        publicTransportResult.transition !== null ||
        publicTransportFleet.tick % Math.round(
            1 / SIMULATION_TIMING.fixedStepSeconds,
          ) === 0
      ) {
        localStorage.setItem(
          PUBLIC_TRANSPORT_SAVE_KEY,
          serializePublicTransportFleet(publicTransportFleet),
        );
      }
      const playerForAudio = state.playersById[LOCAL_PLAYER_ID];
      if (playerForAudio !== undefined) {
        const previousInteriorId = interiorPresence.activeInteriorId;
        interiorPresence = stepInteriorPresence(
          interiorPresence,
          playerForAudio.position,
          !panelSampleActive && playerForAudio.locomotion.mode !== "flying",
        );
        if (interiorPresence.activeInteriorId !== previousInteriorId) {
          renderer.setInteriorPresentation({
            activeInteriorId: interiorPresence.activeInteriorId,
          });
        }
        updateGameplayAudio(playerForAudio, lastSimulationEvents);
      }
      const player = state.playersById[LOCAL_PLAYER_ID];
      if (player !== undefined) {
        const discovery = discoverTraversalSites(traversal, player.position);
        traversal = discovery.state;
        const discovered = discovery.newlyDiscovered[0];
        if (discovered !== undefined) {
          saveTraversalState();
          traversalDiscoveryUi.show(discovered);
          traversalNoticeUntilSeconds =
            state.simulationTimeSeconds + TRAVERSAL_DISCOVERY_VISIBLE_SECONDS;
        } else if (
          traversalNoticeUntilSeconds > 0 &&
          state.simulationTimeSeconds >= traversalNoticeUntilSeconds
        ) {
          traversalNoticeUntilSeconds = 0;
          lockedTraversalNoticeUntilSeconds = 0;
          traversalDiscoveryUi.hide();
        }
        synchronizeCameraCollision(
          player.locomotion.mode,
          mountedBoatId(boatFleet, LOCAL_PLAYER_ID) !== null,
          interiorPresence.activeInteriorId !== null,
        );
        const mountedTransit = mountedPublicTransport(
          publicTransportFleet,
          LOCAL_PLAYER_ID,
        );
        cameraExcludedRoadVehicleColliderIds = [
          ...(mountedRoadVehicle(roadVehicleFleet, LOCAL_PLAYER_ID) === null
            ? []
            : VILNIUS_ROAD_VEHICLES.map(
              (vehicle) => `vehicle:${vehicle.id}`,
            )),
          ...(mountedTransit === null
            ? []
            : [`vehicle:${mountedTransit.presentation.id}`]),
        ];
        const isFlying = player.locomotion.mode === "flying";
        const usesAerialVisibility =
          isFlying || isTvTowerObservationPosition(player.position);
        if (
          !relocating &&
          (
            usesAerialVisibility !== lastTileRequestWasFlying ||
            shouldRefreshTileSelection(
              lastTileRequestPosition,
              player.position,
              manifest.tileSizeM,
              TILE_STREAMING_LOD_REFRESH_DISTANCE_M,
            )
          )
        ) {
          requestTilesAt(player.position, usesAerialVisibility);
        }
      }
      renderer.setSnapshot(toRenderSnapshot(
        state,
        roadVehicleFleet,
        authoredInteriorById(interiorPresence.activeInteriorId)?.cameraDistanceM,
        publicTransportFleet,
      ));
    },

    render(frame): void {
      // Relocation yields while streamed tiles and their Rapier colliders are
      // replaced. Preserve the last stable frame until that transaction has
      // committed so camera casts cannot query a half-migrated physics world.
      if (relocating) {
        return;
      }
      renderer.render(frame.interpolationAlpha);
      const player = state.playersById[LOCAL_PLAYER_ID];
      if (player === undefined) {
        return;
      }
      const streamMetrics = streaming.metrics();
      const physicsStats = physics.stats();
      const environment = renderer.getEnvironmentState();
      const audioStatus = audio.status();
      const activeInterior = authoredInteriorById(
        interiorPresence.activeInteriorId,
      );
      if (!panelSampleActive) {
        updateResidentPresentation(
          player.position,
          environment.timeOfDayHours,
          state.simulationTimeSeconds,
        );
        nearestLockedGate = nearestLockedTraversalGate(
          neighborhood.access,
          player.position,
        );
        if (nearestLockedGate !== null) {
          dialogueUi.setPrompt(nearestLockedGate.gate.label);
        }
        const boatPrompt = boatInteractionPrompt(
          boatFleet,
          LOCAL_PLAYER_ID,
          player.position,
        );
        if (boatPrompt !== null) {
          dialogueUi.setPrompt(boatPrompt);
        }
        const vehiclePrompt = roadVehicleInteractionPrompt(
          roadVehicleFleet,
          LOCAL_PLAYER_ID,
          player.position,
        );
        if (vehiclePrompt !== null) {
          dialogueUi.setPrompt(vehiclePrompt);
        }
        const publicTransportPrompt = player.locomotion.mode === "flying"
          ? null
          : publicTransportInteractionPrompt(
              publicTransportFleet,
              LOCAL_PLAYER_ID,
              player.position,
              terrainHeightForPublicTransport,
            );
        if (publicTransportPrompt !== null) {
          dialogueUi.setPrompt(publicTransportPrompt);
        }
        const liftPrompt = elevatorInteractionPrompt(
          elevatorRide,
          player.position,
        );
        if (liftPrompt !== null) {
          dialogueUi.setPrompt(liftPrompt);
        }
        if (state.simulationTimeSeconds < vehicleExitBlockedUntilSeconds) {
          dialogueUi.setPrompt("All vehicle exits are blocked");
        }
      } else {
        resetResidentPresentationStability();
        nearbyNamedResidentCount = 0;
        ambientPopulationCount = 0;
        nearestInteractableResidentId = null;
        nearestLockedGate = null;
        dialogueUi.setPrompt(null);
        dialogueUi.setObjective(null);
      }
      if (
        devMenu?.isOpen() ||
        radioUi?.isOpen() ||
        appearanceUi?.isOpen() ||
        multiplayerUi?.isOpen() ||
        elevatorRide.phase === "travelling"
      ) {
        dialogueUi.setPrompt(null);
      }
      updateStreetLocationPresentation(player.position);
      const activeBoatId = mountedBoatId(boatFleet, LOCAL_PLAYER_ID);
      const activeBoat = activeBoatId === null
        ? undefined
        : boatFleet.boatsById[activeBoatId];
      const activeRoadVehicle = mountedRoadVehicle(
        roadVehicleFleet,
        LOCAL_PLAYER_ID,
      );
      const activePublicTransport = mountedPublicTransport(
        publicTransportFleet,
        LOCAL_PLAYER_ID,
      );
      const mobileMode = activePublicTransport !== null
        ? "passenger"
        : activeRoadVehicle !== null
        ? activeRoadVehicle.seat.role === "driver"
          ? "driving"
          : "passenger"
        : activeBoat !== undefined
          ? "boating"
          : player.locomotion.mode === "flying"
            ? "flying"
            : "grounded";
      mobileControls.update({
        mode: mobileMode,
        enabled:
          !relocating &&
          devMenu?.isOpen() !== true &&
          radioUi?.isOpen() !== true &&
          appearanceUi?.isOpen() !== true &&
          multiplayerUi?.isOpen() !== true &&
          !dialogueUi.isOpen() &&
          elevatorRide.phase !== "travelling",
      });
      hud.update({
        milestone: `${MILESTONE} · ${state.tick.toLocaleString("en-GB")}`,
        placeLabel: panelSampleActive
          ? "Žirmūnai source-footprint style study · 320 m"
          : "Old Town summer miniature · EPSG:3346 · 256 m cells",
        streetLabel: activeStreetLocation?.name ?? null,
        statusLabel: streamingErrorCount > 0
          ? `${streamingErrorCount} streaming errors`
          : panelSampleActive
            ? "apartment-family benchmark · heights inferred"
            : elevatorRide.phase === "travelling"
              ? `lift · ${elevatorRide.fromStopId} to ${elevatorRide.toStopId}`
            : activePublicTransport !== null
              ? `passenger · ${Math.abs(activePublicTransport.presentation.speedMps * 3.6).toFixed(0)} km/h · ${activePublicTransport.presentation.mode === "bus" ? "bus" : "trolleybus"} ${activePublicTransport.presentation.routeShortName} toward ${activePublicTransport.presentation.headsign}`
            : activeRoadVehicle !== null
              ? `${activeRoadVehicle.seat.role === "driver" ? "driving" : "passenger"} · ${Math.abs(activeRoadVehicle.vehicle.speedMps * 3.6).toFixed(0)} km/h · ${activeRoadVehicle.spec.label}`
            : activeBoat === undefined
              ? activeInterior === null
                ? formatLocomotionStatusLabel(
                    player.locomotion.mode,
                    player.locomotion.surfaceId,
                  )
                : `inside · ${activeInterior.label}`
              : `boating · ${Math.abs(activeBoat.speedMps).toFixed(1)} m/s · ${activeBoat.label}`,
        instructionsVisible,
        presentationSettled: hasEngaged,
        debug: {
          tick: state.tick,
          averageFps: frame.fps,
          simulationHz: 1 / SIMULATION_TIMING.fixedStepSeconds,
          playerX: player.position.x,
          playerY: player.position.y,
          playerZ: player.position.z,
          streaming: {
            residentTiles: streamMetrics.readyTiles,
            requestedTiles:
              streamMetrics.queuedTiles + streamMetrics.loadingTiles,
            visibleLod0Tiles: streamMetrics.readyByLod[0],
            cacheBytes: streamMetrics.readyBytes,
            evictions: streamMetrics.evictions,
          },
          locomotion: {
            mode: player.locomotion.mode,
            surfaceId: player.locomotion.surfaceId,
            staticColliders: physicsStats.staticColliderCount,
            contacts: lastContacts.length,
            footstepSequence: player.locomotion.footstepSequence,
          },
          environment: {
            season: environment.season,
            timeOfDayHours: environment.timeOfDayHours,
            frozen: environment.frozen,
            audioResumed: audioStatus.resumed,
            duskFactor: environment.duskFactor,
            nightFactor: environment.nightFactor,
          },
        },
      });
      const minimapSuppressionReason: MinimapSuppressionReason | null =
        panelSampleActive
          ? "visual-study"
          : elevatorRide.phase === "travelling"
            ? "elevator"
            : activeInterior === null
              ? null
              : "interior";
      minimap.update({
        preferenceVisible: minimapPreferences.visible,
        suppressionReason: minimapSuppressionReason,
        center: player.position,
        facingYaw: player.facingYaw,
        lookYaw: player.lookYaw,
        waypoint: mapMarker,
        streetLabel: activeStreetLocation?.name ?? null,
        tone:
          environment.duskFactor >= 0.7 || environment.nightFactor >= 0.35
            ? "dark"
            : "light",
        tileRevision: minimapTileRevision,
        tiles: [...minimapTiles.values()]
          .map((entry) => entry.features)
          .filter((features) =>
            minimapTileIntersectsCircle(
              features.bounds,
              player.position,
              MINIMAP_CONFIG.radiusM + MINIMAP_CONFIG.overscanM,
            )
          )
          .sort((left, right) => left.tileId.localeCompare(right.tileId)),
      });
      fullMap?.update({
        playerPosition: player.position,
        playerFacingYaw: player.facingYaw,
        playerLocationLabel: activeStreetLocation?.name ?? null,
        marker: mapMarker,
        tone:
          environment.duskFactor >= 0.7 || environment.nightFactor >= 0.35
            ? "dark"
            : "light",
        worldBounds: fullMapOverview?.bounds ?? mapWorldBounds,
        overview: fullMapOverview,
        neighborhoods: fullMapOverview?.neighborhoods ?? [],
        places: fullMapPlaces,
        tileRevision: fullMapFeatureRevision,
        tiles: [...fullMapTiles.values()]
          .map((entry) => entry.features)
          .sort((left, right) => left.tileId.localeCompare(right.tileId)),
        loadingTileCount:
          fullMapActiveLoads.size + fullMapLoadQueue.length,
        loadErrorCount: fullMapLoadErrorCount,
      });
      devMenu?.update({
        season: environment.season,
        timeOfDayHours: environment.timeOfDayHours,
        frozen: environment.frozen,
        surfaceWetness01: environment.surfaceWetness01,
        activeViewMode:
          player.locomotion.mode === "flying" ||
              isTvTowerObservationPosition(player.position)
            ? "flight"
            : "grounded",
        viewSettings: worldViewSettings,
        traversalAccess: {
          blueGate: neighborhood.access.blueGate,
          rooftopShortcut: neighborhood.access.rooftopShortcut,
        },
      });
    },
  });

  let appearanceInspectionLookYaw: number | null = null;
  appearanceUi = createAppearanceCustomizer(
    hudRoot,
    playerAppearance,
    {
      onOpenChange(open): void {
        renderer.setCharacterInspectionView(open ? "front" : null);
        if (open) {
          multiplayerUi?.close();
          fullMap?.close();
          appearanceInspectionLookYaw =
            state.playersById[LOCAL_PLAYER_ID]?.lookYaw ?? null;
          devMenu?.close();
          radioUi?.close();
          input.detach();
          input.resetTouch();
          activeDialogueResidentId = null;
          nearestInteractableResidentId = null;
          dialogueUi.close();
          dialogueUi.setPrompt(null);
          instructionsVisible = false;
          if (document.pointerLockElement !== null) {
            document.exitPointerLock();
          }
        } else {
          if (appearanceInspectionLookYaw !== null) {
            input.resetLookYaw(appearanceInspectionLookYaw);
            appearanceInspectionLookYaw = null;
          }
          if (
            !disposed &&
            fullMap?.isOpen() !== true &&
            devMenu?.isOpen() !== true &&
            radioUi?.isOpen() !== true
          ) {
            input.attach();
            instructionsVisible = true;
            canvas.focus({ preventScroll: true });
          }
        }
      },
      onViewChange(view): void {
        const player = state.playersById[LOCAL_PLAYER_ID];
        if (player === undefined) {
          return;
        }
        input.resetLookYaw(
          player.facingYaw + (view === "back" ? 0 : Math.PI),
        );
        renderer.setCharacterInspectionView(view);
        renderer.resetPitch();
      },
      onChange(appearance): void {
        playerAppearance = structuredClone(appearance);
        renderer.setPlayerAppearance(playerAppearance);
        localStorage.setItem(
          PLAYER_APPEARANCE_SAVE_KEY,
          serializePlayerAppearance(playerAppearance),
        );
        submitRoomCommand({
          kind: "set-appearance",
          appearance: playerAppearance,
        });
      },
    },
  );

  fullMap = createFullMap(hudRoot, {
    onOpenChange(open): void {
      if (open) {
        multiplayerUi?.close();
        appearanceUi?.close();
        devMenu?.close();
        radioUi?.close();
        input.detach();
        input.resetTouch();
        activeDialogueResidentId = null;
        nearestInteractableResidentId = null;
        dialogueUi.close();
        dialogueUi.setPrompt(null);
        instructionsVisible = false;
        if (document.pointerLockElement !== null) {
          document.exitPointerLock();
        }
      } else if (
        !disposed &&
        devMenu?.isOpen() !== true &&
        radioUi?.isOpen() !== true &&
        appearanceUi?.isOpen() !== true
      ) {
        input.attach();
        instructionsVisible = true;
        canvas.focus({ preventScroll: true });
      }
      if (!open) suspendFullMapLoading();
    },
    onViewportChange(viewport): void {
      updateFullMapViewport(viewport);
    },
    onMarkerChange(marker): void {
      mapMarker = marker === null ? null : { ...marker };
      persistMapMarker(localStorage, mapMarker);
    },
    async onTeleport(marker): Promise<void> {
      activeScenario = null;
      activeDialogueResidentId = null;
      nearestInteractableResidentId = null;
      dialogueUi.close();
      dialogueUi.setPrompt(null);
      dialogueUi.setObjective(null);
      renderer.setDistrictVisualSample(null);
      panelSampleActive = false;
      setCameraCollisionEnabled(true);
      const player = state.playersById[LOCAL_PLAYER_ID];
      await moveStateTo(marker, player?.facingYaw ?? 0);
      loop.resetTiming();
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve())
      );
    },
  });

  const devTransferDestinations = [
    ...landmarkDestinations,
    ...TRAVERSAL_REVIEW_DESTINATIONS,
    ROAD_VEHICLE_REVIEW_DESTINATION,
  ].sort((left, right) => left.label.localeCompare(right.label));
  devMenu = createDevMenu(hudRoot, devTransferDestinations, {
    onOpenChange(open): void {
      if (open) {
        multiplayerUi?.close();
        fullMap?.close();
        appearanceUi?.close();
        radioUi?.close();
        input.detach();
        input.resetTouch();
        activeDialogueResidentId = null;
        nearestInteractableResidentId = null;
        dialogueUi.close();
        dialogueUi.setPrompt(null);
        instructionsVisible = false;
        if (document.pointerLockElement !== null) {
          document.exitPointerLock();
        }
      } else if (
        !disposed &&
        fullMap?.isOpen() !== true &&
        radioUi?.isOpen() !== true &&
        appearanceUi?.isOpen() !== true
      ) {
        input.attach();
        instructionsVisible = true;
        canvas.focus({ preventScroll: true });
      }
    },
    onSetTime(hours): void {
      if (submitRoomCommand({
        kind: "set-environment",
        timeOfDayHours: hours,
        frozen: true,
      })) return;
      const environment = renderer.getEnvironmentState();
      renderer.setEnvironment({
        season: environment.season,
        timeOfDayHours: hours,
        frozen: true,
        surfaceWetness01: environment.surfaceWetness01,
      });
    },
    onSetSeason(season): void {
      if (submitRoomCommand({ kind: "set-environment", season })) return;
      const environment = renderer.getEnvironmentState();
      renderer.setEnvironment({
        season,
        timeOfDayHours: environment.timeOfDayHours,
        frozen: environment.frozen,
        surfaceWetness01: environment.surfaceWetness01,
      });
    },
    onSetSurfaceWetness(wetness01): void {
      if (submitRoomCommand({
        kind: "set-environment",
        surfaceWetness01: wetness01,
      })) return;
      const environment = renderer.getEnvironmentState();
      renderer.setEnvironment({
        season: environment.season,
        timeOfDayHours: environment.timeOfDayHours,
        frozen: environment.frozen,
        surfaceWetness01: wetness01,
      });
    },
    onToggleClock(): void {
      const environment = renderer.getEnvironmentState();
      if (submitRoomCommand({
        kind: "set-environment",
        frozen: !environment.frozen,
      })) return;
      renderer.toggleEnvironmentFrozen();
    },
    onSetViewSetting(mode, key, value): void {
      applyWorldViewSettings(
        updateWorldViewSetting(worldViewSettings, mode, key, value),
      );
    },
    onResetViewProfile(mode): void {
      applyWorldViewSettings(
        resetWorldViewProfile(worldViewSettings, mode),
      );
    },
    onSetTraversalPreset(preset): void {
      if (submitRoomCommand({
        kind: "set-neighborhood-preset",
        preset: preset === "blueGate"
          ? "blue-gate"
          : preset === "rooftopShortcut"
          ? "rooftop-shortcut"
          : "locked",
      })) return;
      neighborhood = createNeighborhoodState();
      if (preset === "blueGate") {
        neighborhood = applyNeighborhoodAction(
          neighborhood,
          "accept_ona_parcel",
        );
        neighborhood = applyNeighborhoodAction(
          neighborhood,
          "deliver_ruta_sealed",
        );
      } else if (preset === "rooftopShortcut") {
        neighborhood = applyNeighborhoodAction(
          neighborhood,
          "accept_ona_parcel",
        );
        neighborhood = applyNeighborhoodAction(neighborhood, "entrust_mantas");
      }
      saveNeighborhoodState();
      synchronizeTraversalAccess();
    },
    async onTeleport(destination): Promise<void> {
      activeScenario = null;
      activeDialogueResidentId = null;
      nearestInteractableResidentId = null;
      dialogueUi.close();
      dialogueUi.setPrompt(null);
      dialogueUi.setObjective(null);
      renderer.setDistrictVisualSample(null);
      panelSampleActive = false;
      setCameraCollisionEnabled(true);
      await moveStateTo(
        destination.arrivalPosition,
        destination.facingYaw,
      );
      loop.resetTiming();
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve())
      );
    },
  });

  radioUi = createRadioUi(hudRoot, RADIO_STATIONS, {
    onOpenChange(open): void {
      if (open) {
        fullMap?.close();
        appearanceUi?.close();
        devMenu?.close();
        input.detach();
        input.resetTouch();
        activeDialogueResidentId = null;
        nearestInteractableResidentId = null;
        dialogueUi.close();
        dialogueUi.setPrompt(null);
        instructionsVisible = false;
        if (document.pointerLockElement !== null) {
          document.exitPointerLock();
        }
      } else if (
        !disposed &&
        fullMap?.isOpen() !== true &&
        devMenu?.isOpen() !== true &&
        appearanceUi?.isOpen() !== true
      ) {
        input.attach();
        instructionsVisible = true;
        canvas.focus({ preventScroll: true });
      }
    },
    onSelectStation(stationId): void {
      void radio.selectStation(stationId);
    },
    onSetVolume(volume): void {
      radio.setVolume(volume);
    },
  });
  unsubscribeRadio = radio.subscribe((status) => radioUi?.update(status));

  const onPointerDown = (event: PointerEvent): void => {
    if (
      fullMap?.isOpen() ||
      devMenu?.isOpen() ||
      radioUi?.isOpen() ||
      appearanceUi?.isOpen()
    ) {
      return;
    }
    canvas.focus();
    hasEngaged = true;
    instructionsVisible = false;
    void audio.resume();
    void radio.resume();
    if (event.pointerType === "touch" || event.pointerType === "pen") {
      return;
    }
    void canvas.requestPointerLock().catch(() => {
      // Pointer lock is optional in headless evidence capture and restricted
      // browser contexts; keyboard movement and the visible scene still work.
      instructionsVisible = true;
    });
  };
  const onPointerMove = (event: PointerEvent): void => {
    if (document.pointerLockElement === canvas) {
      input.rotateLookByPixels(event.movementX);
      renderer.setLocalLookYaw(input.lookYaw());
      renderer.pitchBy(event.movementY);
    }
  };
  const onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    renderer.zoomBy(event.deltaY);
  };
  const onResize = (): void => renderer.resize();
  const onPointerLockChange = (): void => {
    if (document.pointerLockElement !== canvas) {
      instructionsVisible =
        fullMap?.isOpen() !== true &&
        multiplayerUi?.isOpen() !== true &&
        devMenu?.isOpen() !== true &&
        radioUi?.isOpen() !== true &&
        appearanceUi?.isOpen() !== true;
    }
  };
  const onEnvironmentDebugKey = (event: KeyboardEvent): void => {
    if (fullMap?.isOpen()) {
      return;
    }
    if (event.code === "KeyN" && !event.repeat) {
      event.preventDefault();
      radioUi?.toggle();
      return;
    }
    if (
      devMenu?.isOpen() ||
      radioUi?.isOpen() ||
      appearanceUi?.isOpen()
    ) {
      return;
    }
    if (
      event.code === "KeyW" ||
      event.code === "KeyA" ||
      event.code === "KeyS" ||
      event.code === "KeyD" ||
      event.code === "ShiftLeft" ||
      event.code === "ShiftRight" ||
      event.code === "Space" ||
      event.code === "KeyE" ||
      event.code === "KeyL"
    ) {
      hasEngaged = true;
      instructionsVisible = false;
    }
    if (event.code === "KeyL" && !event.repeat) {
      event.preventDefault();
      minimapPreferences = {
        schemaVersion: 1,
        visible: !minimapPreferences.visible,
      };
      persistMinimapPreferences(localStorage, minimapPreferences);
    } else if (
      event.code === "BracketLeft" ||
      event.code === "BracketRight"
    ) {
      const environment = renderer.getEnvironmentState();
      const offset = event.code === "BracketLeft"
        ? -ENVIRONMENT_RENDER_CONFIG.scrubStepHours
        : ENVIRONMENT_RENDER_CONFIG.scrubStepHours;
      if (!submitRoomCommand({
        kind: "set-environment",
        timeOfDayHours: (environment.timeOfDayHours + offset + 24) % 24,
      })) {
        renderer.scrubEnvironment(offset);
      }
    } else if (
      dialogueUi.isOpen() &&
      /^Digit[1-9]$/.test(event.code)
    ) {
      const index = Number(event.code.slice(-1)) - 1;
      const button = hudRoot.querySelectorAll<HTMLButtonElement>(
        ".dialogue-choices button",
      )[index];
      button?.click();
    }
  };

  canvas.addEventListener("pointerdown", onPointerDown);
  window.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("wheel", onWheel, { passive: false });
  window.addEventListener("resize", onResize);
  document.addEventListener("pointerlockchange", onPointerLockChange);
  window.addEventListener("keydown", onEnvironmentDebugKey);

  const bridge: VilniusRuntimeBridge = {
    ready: false,

    async applyScenario(scenario): Promise<void> {
      if (disposed) {
        throw new Error("Cannot apply a scenario after disposal.");
      }
      activeDialogueResidentId = null;
      nearestInteractableResidentId = null;
      dialogueUi.close();
      dialogueUi.setPrompt(null);
      dialogueUi.setObjective(null);
      activeScenario = scenario;
      if (multiplayer.connectionStatus === "connected") {
        await multiplayer.sendCommand({
          kind: "set-environment",
          season: scenario.environment.season,
          timeOfDayHours: scenario.environment.timeOfDayHours,
          frozen: true,
        });
      } else {
        renderer.setEnvironment({
          season: scenario.environment.season,
          timeOfDayHours: scenario.environment.timeOfDayHours,
          frozen: true,
        });
      }
      if (scenario.bookmark === "soviet-panel-district") {
        await moveStateTo(STREET_BOOKMARK);
        const player = state.playersById[LOCAL_PLAYER_ID];
        if (player === undefined) {
          throw new Error(`Player ${LOCAL_PLAYER_ID} is missing from game state.`);
        }
        const facingYaw = renderer.setDistrictVisualSample(
          panelSample,
          player.position,
        );
        panelSampleActive = true;
        setCameraCollisionEnabled(false);
        if (facingYaw !== null) {
          input.resetLookYaw(facingYaw);
          state = {
            ...state,
            playersById: {
              ...state.playersById,
              [LOCAL_PLAYER_ID]: {
                ...player,
                facingYaw,
                lookYaw: facingYaw,
              },
            },
          };
          renderer.setSnapshot(toRenderSnapshot(state));
        }
      } else {
        renderer.setDistrictVisualSample(null);
        panelSampleActive = false;
        const player = state.playersById[LOCAL_PLAYER_ID];
        setCameraCollisionEnabled(
          player?.locomotion.mode !== "flying",
        );
        const bookmark = resolveBookmark(scenario.bookmark);
        await moveStateTo(bookmark, bookmark.facingYaw);
      }
      loop.resetTiming();
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    },

    async loadDeferredAuthoredAssets(): Promise<void> {
      if (disposed) {
        throw new Error("Cannot load authored assets after disposal.");
      }
      await renderer.loadDeferredAuthoredAssets();
    },

    async moveToEvidencePoint(position, facingYaw = 0): Promise<void> {
      if (disposed) {
        throw new Error("Cannot traverse after disposal.");
      }
      await moveStateTo(position, facingYaw);
      loop.resetTiming();
    },

    async preparePublicTransportReview(
      sourceRouteId,
      directionId = 0,
    ): Promise<void> {
      if (disposed) {
        throw new Error("Cannot prepare transit review after disposal.");
      }
      const pattern = VILNIUS_PUBLIC_TRANSPORT_PATTERNS.find(
        (candidate) =>
          candidate.sourceRouteId === sourceRouteId &&
          candidate.directionId === directionId,
      );
      if (pattern === undefined) {
        throw new Error(
          `Public-transport pattern ${sourceRouteId}:${directionId} is missing.`,
        );
      }
      const departure = pattern.departuresSeconds.reduce((nearest, candidate) =>
        Math.abs(candidate - PUBLIC_TRANSPORT_TUNING.initialServiceTimeSeconds) <
            Math.abs(nearest - PUBLIC_TRANSPORT_TUNING.initialServiceTimeSeconds)
          ? candidate
          : nearest
      );
      const serviceTimeSeconds = departure + 1;
      const presentation = publicTransportTripPresentationAtTime(
        pattern.id,
        departure,
        serviceTimeSeconds,
      );
      if (presentation === null || !presentation.doorsOpen) {
        throw new Error(`Could not stage ${pattern.id} at its first stop.`);
      }
      publicTransportFleet = {
        ...createInitialPublicTransportFleetState(),
        serviceTimeSeconds,
      };
      const doorSideOffsetM = 2.15;
      const reviewPosition = {
        x: presentation.position.x +
          Math.cos(presentation.yawRadians) * doorSideOffsetM,
        z: presentation.position.z -
          Math.sin(presentation.yawRadians) * doorSideOffsetM,
      };
      await moveStateTo(reviewPosition, presentation.yawRadians);
      loop.resetTiming();
    },

    setPublicTransportReviewTime(serviceTimeSeconds): void {
      if (!Number.isFinite(serviceTimeSeconds) || serviceTimeSeconds < 0) {
        throw new Error("Public-transport review time must be non-negative.");
      }
      publicTransportFleet = {
        ...publicTransportFleet,
        serviceTimeSeconds,
      };
      localStorage.setItem(
        PUBLIC_TRANSPORT_SAVE_KEY,
        serializePublicTransportFleet(publicTransportFleet),
      );
      const player = state.playersById[LOCAL_PLAYER_ID];
      if (player !== undefined) {
        synchronizePublicTransportPresentation(player.position);
      }
    },

    faceEvidenceYaw(facingYaw): void {
      input.resetLookYaw(facingYaw);
    },

    pitchEvidenceCamera(deltaPixelsY): void {
      renderer.pitchBy(deltaPixelsY);
    },

    resetEvidenceCameraPitch(): void {
      renderer.resetPitch();
    },
    resetMultiplayerReconciliationDiagnostics(): void {
      multiplayerReconciliationCorrectionsMeters.length = 0;
      multiplayerMaximumReconciliationSample = null;
    },

    getRenderedPlayerPosition(): Readonly<{
      x: number;
      y: number;
      z: number;
    }> {
      return renderer.getRenderedPlayerPosition();
    },

    getRenderedMotionDiagnostics() {
      return renderer.getRenderedMotionDiagnostics();
    },

    applyNeighborhoodAction(action): void {
      if (submitRoomCommand({ kind: "neighborhood-action", action })) return;
      neighborhood = applyNeighborhoodAction(neighborhood, action);
      saveNeighborhoodState();
      synchronizeTraversalAccess();
    },

    openResidentDialogue(residentId): void {
      openResidentDialogue(residentId);
    },

    resetNeighborhood(): void {
      if (submitRoomCommand({
        kind: "set-neighborhood-preset",
        preset: "locked",
      })) return;
      neighborhood = createNeighborhoodState();
      synchronizeTraversalAccess();
      activeDialogueResidentId = null;
      dialogueUi.close();
      saveNeighborhoodState();
    },

    getState(): VilniusRuntimeState {
      const player = state.playersById[LOCAL_PLAYER_ID];
      if (player === undefined) {
        throw new Error(`Player ${LOCAL_PLAYER_ID} is missing from game state.`);
      }
      const frame = loop.getFrameStats();
      const renderPerformance = renderer.getPerformanceDiagnostics();
      const streamMetrics = streaming.metrics();
      const renderMetrics = renderer.getWorldRenderDiagnostics();
      const environment = renderer.getEnvironmentState();
      const audioStatus = audio.status();
      const radioStatus = radio.status();
      const sortedReconciliationCorrections = [
        ...multiplayerReconciliationCorrectionsMeters,
      ].sort((left, right) => left - right);
      const reconciliationP95Index = Math.max(
        0,
        Math.ceil(sortedReconciliationCorrections.length * 0.95) - 1,
      );
      return {
        activeScenarioId: activeScenario?.id ?? "foundation-default",
        multiplayer: {
          mode: multiplayerSessionMode,
          connectionStatus: multiplayer.connectionStatus,
          roomId: multiplayer.connectedRoom?.id ?? null,
          playerId: multiplayer.connectedPlayerId,
          lastInputMoveX: lastMultiplayerInputMoveX,
          lastInputMoveZ: lastMultiplayerInputMoveZ,
          keyboardAttached: input.diagnostics().keyboardAttached,
          pressedKeyboardCodes: input.diagnostics().pressedKeyboardCodes,
          reconciliationSampleCount: sortedReconciliationCorrections.length,
          reconciliationP95Meters:
            sortedReconciliationCorrections[reconciliationP95Index] ?? 0,
          reconciliationMaximumMeters:
            sortedReconciliationCorrections.at(-1) ?? 0,
          reconciliationMaximumSample:
            multiplayerMaximumReconciliationSample === null
              ? null
              : structuredClone(multiplayerMaximumReconciliationSample),
        },
        player: {
          position: { ...player.position },
          facingYaw: player.facingYaw,
          lookYaw: player.lookYaw,
          velocity: { ...player.velocity },
          locomotion: {
            mode: player.locomotion.mode,
            grounded: player.locomotion.grounded,
            surfaceId: player.locomotion.surfaceId,
            groundSlopeRadians: player.locomotion.groundSlopeRadians,
            landingCount: player.locomotion.landingCount,
            mantleCount: player.locomotion.mantleCount,
            footstepSequence: player.locomotion.footstepSequence,
          },
        },
        frame: {
          fps: frame.fps,
          sampleCount: frame.sampleCount,
          drawCalls: renderPerformance.drawCalls,
          triangles: renderPerformance.triangles,
          geometries: renderPerformance.geometries,
          textures: renderPerformance.textures,
          shaderPrograms: renderPerformance.shaderPrograms,
          frameCpuMs: renderPerformance.frameCpuMs,
          sceneUpdateCpuMs: renderPerformance.sceneUpdateCpuMs,
          drawCpuMs: renderPerformance.drawCpuMs,
          shadowMapUpdateCount: renderPerformance.shadowMapUpdateCount,
          shadowMapUpdatedLastFrame:
            renderPerformance.shadowMapUpdatedLastFrame,
        },
        camera: {
          collisionEnabled: cameraCollisionEnabled,
          excludedRoadVehicleColliderCount:
            cameraExcludedRoadVehicleColliderIds.length,
          farMeters: renderer.getCameraFarMeters(),
          lookYaw: input.lookYaw(),
        },
        streaming: {
          desiredTiles: streamMetrics.desiredTiles,
          queuedTiles: streamMetrics.queuedTiles,
          loadingTiles: streamMetrics.loadingTiles,
          readyTiles: streamMetrics.readyTiles,
          readyLod0Tiles: streamMetrics.readyByLod[0],
          readyTileLods: streaming.getReadyTiles().map((tile) => ({
            tileId: tile.tileId,
            lod: tile.lod,
          })),
          readyBytes: streamMetrics.readyBytes,
          evictions: streamMetrics.evictions,
          disposedPayloads: streamMetrics.disposedPayloads,
          attachedRenderTiles: renderMetrics.attachedTileCount,
          maximumTileAttachMs,
          slowestAttachedTileId,
          slowestTileBuildCpuMsByGroup,
          lastUpdateMs,
          lastSettleMs,
          lastRenderQueueMs,
          renderDrawCallEstimate: renderMetrics.drawCallEstimate,
          renderTriangleCount: renderMetrics.triangleCount,
          renderGeometryCount: renderMetrics.geometryCount,
          renderDrawCallsByGroup: renderMetrics.drawCallsByGroup,
          renderTrianglesByGroup: renderMetrics.trianglesByGroup,
          renderBuildCpuMsByGroup: renderMetrics.buildCpuMsByGroup,
          renderBuildCpuMsByTile: renderMetrics.buildCpuMsByTile,
        },
        physics: {
          ...physics.stats(),
          lastContactCount: lastContacts.length,
          lastEvents: lastSimulationEvents.map((event) =>
            structuredClone(event)
          ),
        },
        environment: {
          season: environment.season,
          timeOfDayHours: environment.timeOfDayHours,
          frozen: environment.frozen,
          surfaceWetness01: environment.surfaceWetness01,
          nightFactor: environment.nightFactor,
          fogFarMeters: environment.fogFarMeters,
        },
        viewSettings: {
          schemaVersion: 1,
          grounded: { ...worldViewSettings.grounded },
          flight: { ...worldViewSettings.flight },
        },
        location: {
          streetName: activeStreetLocation?.name ?? null,
          distanceM: activeStreetLocation?.distanceM ?? null,
        },
        minimap: {
          ...minimap.getDiagnostics(),
          extractionErrorCount: minimapExtractionErrorCount,
          lastExtractionMs: lastMinimapExtractionMs,
          maximumExtractionMs: maximumMinimapExtractionMs,
        },
        map: fullMap?.getDiagnostics() ?? {
          open: false,
          ready: false,
          center: { x: 0, z: 0 },
          radiusM: FULL_MAP_CONFIG.initialRadiusM,
          marker: mapMarker === null ? null : { ...mapMarker },
          tileCount: 0,
          loadingTileCount: 0,
          loadErrorCount: fullMapLoadErrorCount,
          overviewReady: fullMapOverview !== null,
          visibleNeighborhoodCount: 0,
          visiblePlaceCount: 0,
          drawCount: 0,
          lastDrawMs: 0,
          maxDrawMs: 0,
        },
        audio: {
          available: audioStatus.available,
          resumed: audioStatus.resumed,
          muted: audioStatus.muted,
          radioStationId: radioStatus.stationId,
          radioPlaybackState: radioStatus.playbackState,
          radioVolume: radioStatus.volume,
          lastStartedEventCount: lastAudioReport.startedEventIds.length,
          totalStartedEventCount: totalStartedAudioEventCount,
          windLevel: lastAudioReport.ambience.wind,
          riverLevel: lastAudioReport.ambience.river,
          interiorLevel: lastAudioReport.ambience.interior,
          vehicleEngineLevel: lastAudioReport.vehicle.engine,
          vehicleTyreLevel: lastAudioReport.vehicle.tyres,
          vehicleHornLevel: lastAudioReport.vehicle.horn,
          vehicleSirenLevel: lastAudioReport.vehicle.siren,
        },
        interior: {
          activeInteriorId: interiorPresence.activeInteriorId,
          candidateInteriorId: interiorPresence.candidateInteriorId,
          entryCount: interiorPresence.entryCount,
          exitCount: interiorPresence.exitCount,
          lightingMix01: renderer.getInteriorLightingState().mix01,
        },
        elevator: elevatorRide.phase === "travelling"
          ? {
              phase: elevatorRide.phase,
              fromStopId: elevatorRide.fromStopId,
              toStopId: elevatorRide.toStopId,
              progress01: Math.min(
                1,
                elevatorRide.elapsedSeconds / elevatorRide.durationSeconds,
              ),
              rideCount: elevatorRide.rideCount,
            }
          : {
              phase: elevatorRide.phase,
              fromStopId: null,
              toStopId: null,
              progress01: 0,
              rideCount: elevatorRide.rideCount,
            },
        visualStudy: {
          panelSampleId: panelSampleActive ? panelSample.id : null,
          threeCrossesPlaced: true,
          heroLandmarkCount: landmarkDiagnostics.landmarkCount,
          loadedHeroLandmarkIds: landmarkDiagnostics.loadedLandmarkIds,
          simplifiedHeroLandmarkIds: landmarkDiagnostics.simplifiedLandmarkIds,
          authoredBridgeCount: bridgeDiagnostics.landmarkCount + 1,
          electricBoatCount: boatDiagnostics.vesselCount,
          publicTransportAssetModeCount:
            publicTransportDiagnostics.loadedModeCount,
          publicTransportSourceTriangleCount:
            publicTransportDiagnostics.sourceTriangleCount,
          parkedVehicleCount: parkedVehicleDiagnostics.landmarkCount,
          parkedVehicleMeshCount: parkedVehicleDiagnostics.meshCount,
          civicSquareCount: civicSquareDiagnostics.landmarkCount,
          airportAssetCount: airportDiagnostics.landmarkCount,
          airportMeshCount: airportDiagnostics.meshCount,
          traversalAssetCount: traversalDiagnostics.landmarkCount,
          modularCityArchetypeCount:
            modularCityAssetDiagnostics.archetypeCount,
          modularCityPlacementCount:
            modularCityAssetDiagnostics.placementCount,
          modularCityFullDetailArchetypeCount:
            modularCityAssetDiagnostics.fullDetailArchetypeCount,
          modularCityBucketCount:
            modularCityAssetDiagnostics.bucketCount,
          modularCityVisibleBucketCount:
            modularCityAssetDiagnostics.visibleBucketCount,
          modularCityFullDetailBucketCount:
            modularCityAssetDiagnostics.fullDetailBucketCount,
          modularCityMeshCount:
            modularCityAssetDiagnostics.meshCount,
          modularCityInstancedTriangleCount:
            modularCityAssetDiagnostics.instancedTriangleCount,
        },
        boats: {
          mountedBoatId: mountedBoatId(boatFleet, LOCAL_PLAYER_ID),
          vessels: boatPresentations(boatFleet).map((boat) => ({
            id: boat.id,
            position: { ...boat.position },
            yawRadians: boat.yawRadians,
            speedMps: boat.speedMps,
            occupied: boat.occupied,
          })),
        },
        roadVehicles: {
          mountedVehicleId: mountedRoadVehicle(
            roadVehicleFleet,
            LOCAL_PLAYER_ID,
          )?.vehicle.id ?? null,
          mountedSeatId: mountedRoadVehicle(
            roadVehicleFleet,
            LOCAL_PLAYER_ID,
          )?.seat.id ?? null,
          mountedSeatRole: mountedRoadVehicle(
            roadVehicleFleet,
            LOCAL_PLAYER_ID,
          )?.seat.role ?? null,
          mountedTransitionKind: mountedRoadVehicle(
            roadVehicleFleet,
            LOCAL_PLAYER_ID,
          )?.mount.transition?.kind ?? null,
          vehicles: roadVehiclePresentations(roadVehicleFleet).map(
            (vehicle) => ({
              id: vehicle.id,
              position: { ...vehicle.position },
              yawRadians: vehicle.yawRadians,
              pitchRadians: vehicle.pitchRadians,
              rollRadians: vehicle.rollRadians,
              speedMps: vehicle.speedMps,
              steeringRadians: vehicle.steeringRadians,
              occupied: Object.keys(
                roadVehicleFleet.vehiclesById[vehicle.id]
                  ?.occupantBySeatId ?? {},
              ).length > 0,
            }),
          ),
        },
        publicTransport: {
          serviceTimeSeconds: publicTransportFleet.serviceTimeSeconds,
          mountedVehicleId: mountedPublicTransport(
            publicTransportFleet,
            LOCAL_PLAYER_ID,
          )?.presentation.id ?? null,
          mountedPatternId: mountedPublicTransport(
            publicTransportFleet,
            LOCAL_PLAYER_ID,
          )?.mount.patternId ?? null,
          alightRequested: mountedPublicTransport(
            publicTransportFleet,
            LOCAL_PLAYER_ID,
          )?.mount.alightRequested ?? false,
          renderedVehicleCount: currentPublicTransportPresentations.length,
          colliderVehicleCount:
            currentPublicTransportColliderPresentations.length,
          vehicles: currentPublicTransportPresentations.map((vehicle) => ({
            id: vehicle.id,
            patternId: vehicle.patternId,
            mode: vehicle.mode,
            routeShortName: vehicle.routeShortName,
            headsign: vehicle.headsign,
            position: { ...vehicle.position },
            yawRadians: vehicle.yawRadians,
            speedMps: vehicle.speedMps,
            doorsOpen: vehicle.doorsOpen,
            currentStopId: vehicle.currentStopId,
            nextStopId: vehicle.nextStopId,
            occupied: vehicle.occupied,
          })),
        },
        neighborhood: {
          namedResidentCount: NAMED_RESIDENTS.length,
          nearbyFullSimulationCount: nearbyNamedResidentCount,
          ambientPopulationCount,
          unloadedNamedResidentCount:
            NAMED_RESIDENTS.length - nearbyNamedResidentCount,
          activeDialogueResidentId,
          presentedResidents: lastResidentPresentations.map((resident) => ({
            id: resident.id,
            named: resident.named,
            position: { ...resident.position },
          })),
          state: structuredClone(neighborhood),
        },
        traversal: {
          discoveredSiteIds: [...traversal.discoveredSiteIds],
          nearestLockedGateId: nearestLockedGate?.gate.id ?? null,
        },
      };
    },
  };

  loop.start();
  bridge.ready = true;

  return {
    bridge,

    dispose(): void {
      if (disposed) {
        return;
      }
      disposed = true;
      if (authoredAssetBackgroundTimer !== null) {
        clearTimeout(authoredAssetBackgroundTimer);
        authoredAssetBackgroundTimer = null;
      }
      bridge.ready = false;
      loop.stop();
      multiplayer.dispose();
      multiplayerUi?.dispose();
      multiplayerUi = null;
      fullMap?.dispose();
      fullMap = null;
      fullMapOverview?.dispose();
      appearanceUi?.dispose();
      appearanceUi = null;
      devMenu?.dispose();
      devMenu = null;
      unsubscribeRadio?.();
      unsubscribeRadio = null;
      radioUi?.dispose();
      radioUi = null;
      unsubscribeStreaming();
      streaming.dispose();
      for (const controller of fullMapActiveLoads.values()) {
        controller.abort();
      }
      fullMapActiveLoads.clear();
      fullMapLoadQueue = [];
      fullMapTileSource.dispose();
      tileSource.dispose();
      physics.dispose();
      input.detach();
      input.resetTouch();
      canvas.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("wheel", onWheel);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("pointerlockchange", onPointerLockChange);
      window.removeEventListener("keydown", onEnvironmentDebugKey);
      radio.dispose();
      void audio.dispose();
      dialogueUi.dispose();
      elevatorUi.dispose();
      traversalDiscoveryUi.dispose();
      mobileControls.dispose();
      minimap.dispose();
      hud.dispose();
      renderer.dispose();
    },
  };
}
